import React, { useEffect, useRef, useState } from 'react';
import { recipeUnitCost } from '../../utils/auxiliaryRecipe';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchSemiFinished, createSemiFinished, updateSemiFinished, deleteSemiFinished, bulkDeleteSemiFinished,
  fetchSemiFinishedCategories,
} from '../../store/slices/semiFinishedSlice';
import { createProduct, fetchProductCategories, fetchProducts } from '../../store/slices/productSlice';
import { fetchDishes, recalculateDishAndSemiCosts, updateDish } from '../../store/slices/dishSlice';
import { fetchPreparationOperations } from '../../store/slices/preparationOperationSlice';
import { Dish, ProcessingMethod, RecipePreparationStep, SemiFinishedProduct, SemiFinishedProduction, SemiFinishedUsageReportEntry } from '../../types';
import { getAllergenBadges, getAllergenIcon } from '../../constants/allergenIcons';
import api, { OPERATING_GROUP_STORAGE_KEY } from '../../api/api';
import { getSelectableProducts } from '../../utils/selectableProducts';
import { parseRecipeImportNumber, parseRecipePreparationSteps, uniqueRecipeMatch } from '../../utils/catalogRecipeImport';
import { extractApiErrorMessage } from '../../utils/apiError';
import B52Modal from '../../components/B52Modal';
import B52Autocomplete from '../../components/B52Autocomplete';
import SendPdfToTelegramButton from '../../components/SendPdfToTelegramButton';
import ItemQuantityField from '../../components/ItemQuantityField';
import RecipeCalculationCard from '../../components/RecipeCalculationCard';
import RecipePreparationStepsEditor from '../../components/RecipePreparationStepsEditor';
import { openAndPrintHtml } from '../../utils/printDocument';
import useManualProductionOperation from './useManualProductionOperation';
import './dish-recipe-mobile.css';

const WEIGHT_TYPES = [
  { value: 'kg', label: 'кг' }, { value: 'g', label: 'г' },
  { value: 'l', label: 'л' }, { value: 'ml', label: 'мл' }, { value: 'pcs', label: 'шт' },
];
const PAGE_SIZE = 15;
type SemiFinishedSort = 'name-asc' | 'name-desc' | 'cost-asc' | 'cost-desc' | 'category-asc' | 'category-desc';
const hiddenIngredientColumns = {
  loss: false,
  price: false,
  cost: false,
  notes: false,
  processing: false,
};

// ── Portal-based autocomplete ─────────────────────────────────
interface SearchItem { id: string; name: string; }

interface SearchSelectProps {
  items: SearchItem[];
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
}

const SearchSelect: React.FC<SearchSelectProps> = ({ items, value, onChange, placeholder }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = items.find(i => i.id === value);

  const calcPos = () => {
    if (wrapRef.current) {
      const r = wrapRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 2, left: r.left, width: r.width });
    }
  };

  const handleOpen = () => { calcPos(); setOpen(true); setTimeout(() => inputRef.current?.focus(), 10); };
  const handleClose = () => { setOpen(false); setQuery(''); };

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapRef.current && !wrapRef.current.contains(target)) {
        const portal = document.getElementById('sf-search-portal');
        if (portal && !portal.contains(target)) handleClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = () => calcPos();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => { window.removeEventListener('scroll', handler, true); window.removeEventListener('resize', handler); };
  }, [open]);

  const filtered = query.trim()
    ? items.filter(i => i.name.toLowerCase().includes(query.toLowerCase()))
    : items;

  const select = (item: SearchItem) => { onChange(item.id); handleClose(); };
  const clear = (e: React.MouseEvent) => { e.stopPropagation(); onChange(''); setQuery(''); };

  const inpStyle: React.CSSProperties = {
    border: '1px solid var(--crm-border)', borderRadius: '6px', padding: '5px 8px',
    width: '100%', boxSizing: 'border-box', fontSize: '13px', background: 'var(--crm-input-bg)', color: 'var(--crm-text)',
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      {selected && !open ? (
        <div style={{ ...inpStyle, display: 'flex', alignItems: 'center', gap: '4px', cursor: 'text', minHeight: '30px' }}
          onClick={handleOpen}>
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected.name}</span>
          <button type="button" onClick={clear} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--crm-text-muted)', fontSize: '16px', padding: '0 2px', lineHeight: 1 }}>×</button>
        </div>
      ) : (
        <input ref={inputRef} style={inpStyle} value={query}
          placeholder={placeholder ?? '🔍 Пошук...'}
          onChange={e => { setQuery(e.target.value); if (!open) { calcPos(); setOpen(true); } }}
          onFocus={() => { if (!open) { calcPos(); setOpen(true); } }}
        />
      )}

      {open && createPortal(
        <div id="sf-search-portal" style={{
          position: 'fixed', top: pos.top, left: pos.left, width: pos.width,
          zIndex: 99999, background: 'var(--crm-surface)', border: '1px solid var(--crm-border)', borderRadius: '6px',
          boxShadow: 'var(--crm-shadow-sm)', maxHeight: '220px', overflowY: 'auto',
        }}>
          {filtered.length === 0
            ? <div style={{ padding: '10px 12px', color: 'var(--crm-text-muted)', fontSize: '13px' }}>Нічого не знайдено</div>
            : filtered.map(i => (
                <div key={i.id} onMouseDown={() => select(i)}
                  style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid var(--crm-table-row-border)', color: 'var(--crm-text)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--crm-surface-alt)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  {i.name}
                </div>
              ))}
        </div>,
        document.body
      )}
    </div>
  );
};
// ─────────────────────────────────────────────────────────────

interface IngredientRow {
  _key: number;
  type: 'product' | 'semi';
  refId: string;
  bruttoWeight: string;
  nettoWeight: string;
  output: string;
  notes: string;
  operationNotes: string;
  preparationOperationId: string;
  processingMethodId: string;
  processingTemperatureC: string;
}

const emptyRow = (key: number): IngredientRow =>
  ({ _key: key, type: 'product', refId: '', bruttoWeight: '', nettoWeight: '', output: '', notes: '', operationNotes: '', preparationOperationId: '', processingMethodId: '', processingTemperatureC: '' });

interface FormState {
  name: string;
  b52Code: string;
  b52Name: string;
  categoryId: string;
  cookingTime: string;
  serveTime: string;
  weightType: string;
  finalOutput: string;
  description: string;
  excludeFromDishWeightCalc: boolean;
  isAuxiliaryRecipe: boolean;
  preparationSteps: RecipePreparationStep[];
}
const emptyForm: FormState = {
  name: '',
  b52Code: '',
  b52Name: '',
  categoryId: '',
  cookingTime: '',
  serveTime: '',
  weightType: 'kg',
  finalOutput: '',
  description: '',
  excludeFromDishWeightCalc: false,
  isAuxiliaryRecipe: false,
  preparationSteps: [],
};

type UsageEntry = {
  kind: 'dish' | 'semi';
  id: number;
  name: string;
  quantity: number;
};
type SemiDetailsModalState = {
  semi: SemiFinishedProduct;
  usageEntries: UsageEntry[];
  usageReportEntries: SemiFinishedUsageReportEntry[];
  productionEntries: SemiFinishedProduction[];
  reportLoading: boolean;
  reportError: string | null;
  activeReportTab: 'usage-report' | 'production';
};
type DeleteBlockModalState = {
  title: string;
  message: string;
  entries: UsageEntry[];
  semiFinishedId?: number;
};
type SavedPrintSelection = {
  id: string;
  name: string;
  selectedIds: number[];
  categoryIds: number[];
};

const SAVED_PRINT_SELECTIONS_KEY = 'crm4chef.semifinished.saved-print-selections.v1';

const SemiFinishedPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { items, categories, loading } = useAppSelector((s) => s.semiFinished);
  const products = useAppSelector((s) => s.product.products);
  const productCategories = useAppSelector((s) => s.product.categories);
  const dishes = useAppSelector((s) => s.dish.items);
  const preparationOperations = useAppSelector((s) => s.preparationOperation.items);
  const [processingMethods, setProcessingMethods] = useState<ProcessingMethod[]>([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [b52ModalOpen, setB52ModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>({ ...emptyForm });
  const [rows, setRows] = useState<IngredientRow[]>([]);
  const [ingredientColumns, setIngredientColumns] = useState(hiddenIngredientColumns);
  const [rowKey, setRowKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveArchiveSnapshot, setSaveArchiveSnapshot] = useState(false);
  const [archiveEffectiveFromUtc, setArchiveEffectiveFromUtc] = useState<string>(new Date().toISOString().slice(0, 10));
  const [archiveVersions, setArchiveVersions] = useState<Array<{ id: number; versionNumber: number; effectiveFromUtc: string; effectiveToUtc?: string | null; createdAtUtc: string; createdBy?: string | null; isManualArchive: boolean; snapshotJson?: string | null }>>([]);
  const [selectedArchiveVersionId, setSelectedArchiveVersionId] = useState<number | null>(null);
  const [importMsg, setImportMsg] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [recalculatingCosts, setRecalculatingCosts] = useState(false);
  const [scaleBasis, setScaleBasis] = useState<'brutto' | 'netto' | 'output'>('brutto');
  const [scaleTargetWeight, setScaleTargetWeight] = useState('');
  const [showQuickProduct, setShowQuickProduct] = useState(false);
  const [creatingProduct, setCreatingProduct] = useState(false);
  const [quickProduct, setQuickProduct] = useState({ name: '', price: '', weightType: 'kg', categoryId: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<SemiFinishedSort>('name-asc');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [replaceSource, setReplaceSource] = useState<SemiFinishedProduct | null>(null);
  const [replaceTargetId, setReplaceTargetId] = useState('');
  const [deleteReplaceTargetId, setDeleteReplaceTargetId] = useState('');
  const [replacing, setReplacing] = useState(false);
  const [detailsModal, setDetailsModal] = useState<SemiDetailsModalState | null>(null);
  const [detailsPortions, setDetailsPortions] = useState('1');
  const printPortions = '1';
  const [detailsWorkStart, setDetailsWorkStart] = useState('');
  const [detailsWorkEnd, setDetailsWorkEnd] = useState('');
  const [detailsWorkPortions, setDetailsWorkPortions] = useState('');
  const [productionFormOpen, setProductionFormOpen] = useState(false);
  const [productionQuantity, setProductionQuantity] = useState('');
  const [productionDate, setProductionDate] = useState(new Date().toISOString().slice(0, 10));
  const [productionNotes, setProductionNotes] = useState('');
  const detailsRequest = useRef(0);
  const manualProduction = useManualProductionOperation(() => {
    detailsRequest.current += 1;
    setDetailsModal(null);
    setProductionFormOpen(false);
    setProductionQuantity('');
    setProductionNotes('');
    setSelectedIds([]);
    setError('Робочий ресторан змінено. Деталі виробництва очищено; оновіть сторінку для продовження.');
  }, detailsModal?.semi.id ?? null);
  const { saving: productionSaving, error: productionError, setError: setProductionError } = manualProduction;
  const productionOperation = detailsModal ? manualProduction.operationFor(detailsModal.semi.id) : undefined;
  const [usageModal, setUsageModal] = useState<{ title: string; entries: UsageEntry[] } | null>(null);
  const [deleteBlockModal, setDeleteBlockModal] = useState<DeleteBlockModalState | null>(null);
  const [printManageModalOpen, setPrintManageModalOpen] = useState(false);
  const [savedPrintSelections, setSavedPrintSelections] = useState<SavedPrintSelection[]>([]);
  const [printSelectionName, setPrintSelectionName] = useState('');
  const [printCategoryIds, setPrintCategoryIds] = useState<number[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    dispatch(fetchSemiFinished());
    dispatch(fetchSemiFinishedCategories());
    dispatch(fetchProducts());
    dispatch(fetchProductCategories());
    dispatch(fetchDishes());
    dispatch(fetchPreparationOperations());
    api.get<ProcessingMethod[]>('/processing-methods')
      .then((res) => setProcessingMethods(res.data))
      .catch(() => setProcessingMethods([]));
  }, [dispatch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    if (modalOpen) setIngredientColumns(hiddenIngredientColumns);
  }, [modalOpen]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(SAVED_PRINT_SELECTIONS_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as SavedPrintSelection[];
      if (Array.isArray(parsed)) setSavedPrintSelections(parsed);
    } catch {
      // ignore invalid local storage
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(SAVED_PRINT_SELECTIONS_KEY, JSON.stringify(savedPrintSelections));
    } catch {
      // ignore storage errors
    }
  }, [savedPrintSelections]);

  const formatDurationInput = (seconds: number | null | undefined) => {
    const normalized = Number(seconds ?? 0);
    if (!Number.isFinite(normalized) || normalized <= 0) return '';
    const safe = Math.max(0, Math.floor(normalized));
    const minutes = Math.floor(safe / 60);
    const restSeconds = safe % 60;
    return `${minutes},${restSeconds}`;
  };

  const parseDurationToSeconds = (rawValue: string, fallback = 0) => {
    const value = rawValue.trim();
    if (!value) return fallback;
    const normalized = value.replace(/\s+/g, '');
    if (normalized.includes(',') || normalized.includes(':')) {
      const [minutesPart, secondsPart = '0'] = normalized.split(/[,:]/);
      const minutes = parseInt(minutesPart, 10);
      const normalizedSecondsPart = secondsPart.length === 1 ? `${secondsPart}0` : secondsPart;
      const seconds = parseInt(normalizedSecondsPart, 10);
      if (Number.isNaN(minutes) || Number.isNaN(seconds)) return fallback;
      if (minutes < 0 || seconds < 0) return fallback;
      const minuteCarry = Math.floor(seconds / 60);
      const normalizedSeconds = seconds % 60;
      return minutes * 60 + minuteCarry * 60 + normalizedSeconds;
    }
    const totalSeconds = parseInt(normalized, 10);
    return Number.isNaN(totalSeconds) ? fallback : Math.max(0, totalSeconds);
  };

  const formatDurationLabel = (seconds: number | null | undefined) => {
    const normalized = Number(seconds ?? 0);
    if (!Number.isFinite(normalized) || normalized <= 0) return '—';
    const safe = Math.max(0, Math.floor(normalized));
    const hours = Math.floor(safe / 3600);
    const minutes = Math.floor((safe % 3600) / 60);
    const restSeconds = safe % 60;
    if (hours > 0) return `${hours} год ${minutes} хв ${restSeconds} сек`;
    return `${minutes} хв ${restSeconds} сек`;
  };
  const calcRequiredTime = (secondsPerPortion: number | null | undefined, portions: number) => {
    const safeSeconds = Number.isFinite(Number(secondsPerPortion)) ? Math.max(0, Number(secondsPerPortion)) : 0;
    const safePortions = Number.isFinite(portions) ? Math.max(0, portions) : 0;
    return safeSeconds * safePortions;
  };
  const parseClockToSeconds = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parts = trimmed.split(':');
    if (parts.length !== 2) return null;
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
    return hours * 3600 + minutes * 60;
  };

  const selectableIngredientProducts = React.useMemo(
    () => getSelectableProducts(
      products,
      rows.filter((row) => row.type === 'product').map((row) => row.refId)
    ),
    [products, rows]
  );

  // Combined ingredient search list: products + semi-finished (excluding self)
  const allIngredients = React.useMemo(() => [
    ...selectableIngredientProducts.map(p => ({ id: `p:${p.id}`, name: p.name })),
    ...items
      .filter(sf => sf.id !== editingId)
      .map(sf => ({ id: `s:${sf.id}`, name: `${sf.name} [Н/Ф]` })),
  ], [selectableIngredientProducts, items, editingId]);

  const decodeIngredientId = (encoded: string) => {
    if (!encoded) return { type: 'product' as const, refId: '' };
    if (encoded.startsWith('p:')) return { type: 'product' as const, refId: encoded.slice(2) };
    return { type: 'semi' as const, refId: encoded.slice(2) };
  };

  const encodeIngredientId = (row: IngredientRow) =>
    row.refId ? `${row.type === 'product' ? 'p' : 's'}:${row.refId}` : '';

  const nextKey = () => { const k = rowKey + 1; setRowKey(k); return k; };
  const formatArchiveVersionDate = (value?: string | null) => {
    if (!value) return '—';
    const datePart = String(value).split('T')[0];
    if (!datePart || datePart === '0001-01-01') return '—';
    const parsed = new Date(`${datePart}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? datePart : parsed.toLocaleDateString('uk-UA');
  };

  const restoreArchiveVersion = (version: { id: number; snapshotJson?: string | null }) => {
    if (!version.snapshotJson) {
      setError('Архівна версія не містить даних для відновлення.');
      return;
    }

    try {
      const parsed = JSON.parse(version.snapshotJson) as {
        name?: string;
        Name?: string;
        isAuxiliaryRecipe?: boolean;
        IsAuxiliaryRecipe?: boolean;
        netWeight?: number | string;
        NetWeight?: number | string;
        grossWeight?: number | string;
        GrossWeight?: number | string;
        items?: Array<any>;
        Items?: Array<any>;
      };
      const archivedItems = parsed.items ?? parsed.Items ?? [];
      const restoredRows = archivedItems.map((item, index) => {
        const productId = item.productId ?? item.ProductId ?? null;
        const semiId = item.subSemiFinishedProductId ?? item.SubSemiFinishedProductId ?? null;
        return {
          _key: index + 1,
          type: productId != null ? 'product' : 'semi',
          refId: String(productId ?? semiId ?? ''),
          bruttoWeight: String(Number(item.bruttoWeight ?? item.BruttoWeight ?? item.quantity ?? item.Quantity ?? 0) || 0),
          nettoWeight: String(Number(item.nettoWeight ?? item.NettoWeight ?? 0) || 0),
          output: String(Number(item.output ?? item.Output ?? 0) || 0),
          notes: item.notes ?? item.Notes ?? '',
          operationNotes: item.operationNotes ?? item.OperationNotes ?? '',
          preparationOperationId: (item.preparationOperationId ?? item.PreparationOperationId) != null ? String(item.preparationOperationId ?? item.PreparationOperationId) : '',
          processingMethodId: (item.processingMethodId ?? item.ProcessingMethodId) != null ? String(item.processingMethodId ?? item.ProcessingMethodId) : '',
          processingTemperatureC: (item.processingTemperatureC ?? item.ProcessingTemperatureC) != null ? String(item.processingTemperatureC ?? item.ProcessingTemperatureC) : '',
        } satisfies IngredientRow;
      });

      setForm((prev) => ({
        ...prev,
        name: parsed.name || parsed.Name || prev.name,
        isAuxiliaryRecipe: parsed.isAuxiliaryRecipe ?? parsed.IsAuxiliaryRecipe ?? prev.isAuxiliaryRecipe,
        finalOutput: String(parsed.netWeight ?? parsed.NetWeight ?? parsed.grossWeight ?? parsed.GrossWeight ?? prev.finalOutput ?? 0),
      }));
      setRows(restoredRows.length > 0 ? restoredRows : [emptyRow(0)]);
      setRowKey(restoredRows.length > 0 ? restoredRows.length : 1);
      setSelectedArchiveVersionId(version.id);
      setSaveArchiveSnapshot(false);
      setError('Архівну калькуляцію завантажено в форму для редагування. Збережіть її як поточну версію.');
    } catch {
      setError('Не вдалося розпакувати архівну калькуляцію.');
    }
  };

  const loadArchiveVersions = async (entityType: string, entityId: number) => {
    if (!entityId) {
      setArchiveVersions([]);
      setSelectedArchiveVersionId(null);
      return;
    }

    try {
      const { data } = await api.get<typeof archiveVersions>(`/RecipeVersionSnapshots/${encodeURIComponent(entityType)}/${entityId}`);
      const normalized = Array.isArray(data) ? data : [];
      setArchiveVersions(normalized);
      setSelectedArchiveVersionId((prev) =>
        prev != null && normalized.some((version) => version.id === prev)
          ? prev
          : (normalized[0]?.id ?? null));
    } catch {
      setArchiveVersions([]);
      setSelectedArchiveVersionId(null);
      setError('Не вдалося завантажити архівні версії калькуляції.');
    }
  };

  const deleteArchiveVersion = async (version: { id: number; versionNumber: number }) => {
    if (!editingId || !window.confirm(`Видалити архівну калькуляцію v${version.versionNumber}? Цю дію неможливо скасувати.`)) return;
    try {
      setError(null);
      await api.delete(`/RecipeVersionSnapshots/${version.id}`);
      await loadArchiveVersions('SemiFinishedProduct', editingId);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Не вдалося видалити архівну калькуляцію.');
    }
  };

  const openNew = () => {    setEditingId(null); setSaveArchiveSnapshot(false); setArchiveEffectiveFromUtc(new Date().toISOString().slice(0, 10)); setArchiveVersions([]); setSelectedArchiveVersionId(null); setForm({ ...emptyForm }); setRows([emptyRow(0)]); setRowKey(1); setError(null); setModalOpen(true);
    setScaleBasis('brutto');
    setScaleTargetWeight('');
    setShowQuickProduct(false);
    setCreatingProduct(false);
    setQuickProduct({ name: '', price: '', weightType: 'kg', categoryId: '' });
  };

  const openEdit = (sf: SemiFinishedProduct) => {
    setEditingId(sf.id); setSaveArchiveSnapshot(false); setArchiveEffectiveFromUtc(new Date().toISOString().slice(0, 10)); loadArchiveVersions('SemiFinishedProduct', sf.id);
    setForm({
      name: sf.name,
      b52Code: sf.b52Code ?? '',
      b52Name: sf.b52Name ?? '',
      categoryId: sf.categoryId != null ? String(sf.categoryId) : '',
      cookingTime: formatDurationInput(sf.cookingTime),
      serveTime: formatDurationInput(sf.serveTime),
      weightType: sf.weightType,
      finalOutput: String(sf.netWeight ?? 0),
      description: sf.description,
      excludeFromDishWeightCalc: sf.excludeFromDishWeightCalc ?? false,
      isAuxiliaryRecipe: sf.isAuxiliaryRecipe ?? false,
      preparationSteps: sf.preparationSteps ?? [],
    });
    let k = 0;
    setRows(sf.items.map(i => ({
      _key: k++, type: i.productId != null ? 'product' : 'semi',
      refId: String(i.productId ?? i.subSemiFinishedProductId ?? ''),
      bruttoWeight: String(i.bruttoWeight), nettoWeight: String(i.nettoWeight), output: String(i.output),
      notes: i.notes ?? '',
      operationNotes: i.operationNotes ?? '',
      preparationOperationId: i.preparationOperationId != null ? String(i.preparationOperationId) : '',
      processingMethodId: i.processingMethodId != null ? String(i.processingMethodId) : '',
      processingTemperatureC: i.processingTemperatureC != null ? String(i.processingTemperatureC) : '',
    })));
    setScaleBasis('brutto');
    setScaleTargetWeight('');
    setRowKey(k); setError(null); setModalOpen(true);
    setShowQuickProduct(false);
    setCreatingProduct(false);
    setQuickProduct({ name: '', price: '', weightType: 'kg', categoryId: '' });
  };

  const closeModal = () => {
    setModalOpen(false); setError(null);
    setShowQuickProduct(false);
    setCreatingProduct(false);
    setQuickProduct({ name: '', price: '', weightType: 'kg', categoryId: '' });
  };
  const addRow = () => setRows(r => [...r, emptyRow(nextKey())]);
  const getSemiComputedAllergens = (item: SemiFinishedProduct) => {
    const fromIngredients = item.items.flatMap((row) => {
      if (row.productId != null) return products.find((p) => p.id === row.productId)?.allergens ?? [];
      if (row.subSemiFinishedProductId != null) return items.find((s) => s.id === row.subSemiFinishedProductId)?.allergens ?? [];
      return [];
    });
    const merged = [...fromIngredients, ...(item.allergens ?? [])];
    return merged.filter((allergen, index, arr) => arr.findIndex((entry) => entry.id === allergen.id) === index);
  };
  const removeRow = (key: number) => setRows(r => r.filter(x => x._key !== key));
  const updateRow = (key: number, patch: Partial<IngredientRow>) =>
    setRows(r => r.map(x => x._key === key ? { ...x, ...patch } : x));

  const handleQuickCreateProduct = async () => {
    const name = quickProduct.name.trim();
    const price = parseFloat(quickProduct.price);
    if (!name) { setError("Вкажіть назву нового продукту"); return; }
    if (Number.isNaN(price) || price < 0) { setError('Вкажіть коректну ціну продукту'); return; }

    setCreatingProduct(true);
    setError(null);
    try {
      const created = await dispatch(createProduct({
        name,
        price,
        weightType: quickProduct.weightType,
        categoryId: quickProduct.categoryId ? parseInt(quickProduct.categoryId, 10) : null,
        supplierId: null,
        description: '',
        isActive: true,
        allergenIds: [],
      })).unwrap();

      await dispatch(fetchProducts());
      setRows((prev) => {
        const emptyProductRowIndex = prev.findIndex((r) => !r.refId);
        if (emptyProductRowIndex === -1) return prev;
        const next = [...prev];
        next[emptyProductRowIndex] = { ...next[emptyProductRowIndex], type: 'product', refId: String(created.id) };
        return next;
      });
      setQuickProduct({ name: '', price: '', weightType: 'kg', categoryId: '' });
      setShowQuickProduct(false);
    } catch {
      setError('Не вдалося створити продукт');
    } finally {
      setCreatingProduct(false);
    }
  };

  // Returns effective unit price for a semi-finished product based on its factual output.
  const getSFPrice = (sfId: number): number => {
    const sf = items.find(i => i.id === sfId);
    if (!sf) return 0;
    const totalCost = sf.costPrice > 0 ? sf.costPrice : sf.items.reduce((sum, item) => {
      if (item.productId != null) {
        const p = products.find(pr => pr.id === item.productId);
        return sum + (p?.price ?? 0) * item.bruttoWeight;
      }
      if (item.subSemiFinishedProductId != null) {
        return sum + getSFPrice(item.subSemiFinishedProductId) * item.bruttoWeight;
      }
      return sum;
    }, 0);
    const factualOutput = Number(sf.netWeight) || 0;
    return recipeUnitCost(totalCost, factualOutput, sf.isAuxiliaryRecipe);
  };

  const calcCost = (rowsArg = rows) => {
    let cost = 0;
    for (const row of rowsArg) {
      const brutto = parseFloat(row.bruttoWeight) || 0;
      if (row.type === 'product') {
        const p = products.find(x => x.id === parseInt(row.refId));
        if (p) cost += p.price * brutto;
      } else {
        cost += getSFPrice(parseInt(row.refId)) * brutto;
      }
    }
    return cost;
  };

  const calcRowLossPercent = (row: IngredientRow) => {
    const brutto = parseFloat(row.bruttoWeight) || 0;
    const output = parseFloat(row.output) || 0;
    if (brutto <= 0) return 0;
    return ((brutto - output) / brutto) * 100;
  };

  const totalBrutto = rows.reduce((s, r) => s + (parseFloat(r.bruttoWeight) || 0), 0);
  const totalNetto  = rows.reduce((s, r) => s + (parseFloat(r.nettoWeight) || 0), 0);
  const totalOutput = rows.reduce((s, r) => s + (parseFloat(r.output) || 0), 0);
  const rowsWithBrutto = rows.filter(r => (parseFloat(r.bruttoWeight) || 0) > 0);
  const averageLossPercent = rowsWithBrutto.length > 0
    ? rowsWithBrutto.reduce((s, r) => s + calcRowLossPercent(r), 0) / rowsWithBrutto.length
    : 0;
  const totalLossPercent = totalBrutto > 0 ? ((totalBrutto - totalOutput) / totalBrutto) * 100 : 0;
  const factualOutput = parseFloat(form.finalOutput) || 0;

  const toWeightString = (value: number) => {
    const rounded = Math.max(0, Math.round(value * 1000) / 1000);
    return rounded.toFixed(3).replace(/\.?0+$/, '');
  };

  const handleScaleIngredients = () => {
    const target = parseFloat(scaleTargetWeight.replace(',', '.'));
    if (!Number.isFinite(target) || target <= 0) {
      setError('Вкажіть цільову вагу більше 0.');
      return;
    }

    const currentTotal = scaleBasis === 'brutto'
      ? totalBrutto
      : (scaleBasis === 'netto' ? totalNetto : totalOutput);

    if (currentTotal <= 0) {
      setError(`Неможливо перерахувати: поточна сума по "${scaleBasis === 'brutto' ? 'брутто' : scaleBasis === 'netto' ? 'нетто' : 'виходу'}" дорівнює 0.`);
      return;
    }

    const ratio = target / currentTotal;
    setRows((prev) => prev.map((row) => {
      const brutto = parseFloat(row.bruttoWeight) || 0;
      const netto = parseFloat(row.nettoWeight) || 0;
      const output = parseFloat(row.output) || 0;
      return {
        ...row,
        bruttoWeight: toWeightString(brutto * ratio),
        nettoWeight: toWeightString(netto * ratio),
        output: toWeightString(output * ratio),
      };
    }));
    setError(null);
  };

  const openPrintWindow = (html: string) => {
    void openAndPrintHtml(html, {
      windowFeatures: 'width=950,height=780',
      minimumDelayMs: 500,
      onPopupBlocked: () => alert('Дозвольте спливаючі вікна для друку'),
    });
  };

  const buildPrintHtml = (
    name: string,
    syncCode: string,
    catName: string | null,
    weightType: string,
    cookingTime: number,
    serveTime: number,
    description: string,
    ingRows: Array<{ label: string; note?: string; brutto: number; netto: number; output: number; rowLoss: number; unitPrice: number; rowCost: number }>,
    tBrutto: number,
    tNetto: number,
    tOutput: number,
    totalCost: number,
    factualOut: number,
    allergens: string[],
    portions = 1
  ) => {
    const rowsHtml = ingRows.map((r, i) => `
      <tr style="border-bottom:${r.note ? "0" : "1px solid #eee"};">
        <td style="padding:5px 8px;text-align:center;">${i + 1}</td>
        <td style="padding:5px 8px;">${r.label}</td>
        <td style="padding:5px 8px;text-align:right;">${r.brutto.toFixed(3)}</td>
        <td style="padding:5px 8px;text-align:right;">${r.netto.toFixed(3)}</td>
        <td style="padding:5px 8px;text-align:right;">${r.output.toFixed(3)}</td>
        <td style="padding:5px 8px;text-align:right;color:#d35400;">${r.rowLoss.toFixed(2)}%</td>
        <td style="padding:5px 8px;text-align:right;color:#555;">${r.unitPrice.toFixed(2)}</td>
        <td style="padding:5px 8px;text-align:right;color:#1a7a3c;font-weight:600;">${r.rowCost.toFixed(2)}</td>
      </tr>
      ${r.note ? `<tr style="border-bottom:1px solid #eee;">
        <td></td>
        <td style="padding:2px 8px 6px 8px;color:#607d8b;font-size:11px;font-style:italic;">Нотатка: ${r.note}</td>
        <td colspan="6"></td>
      </tr>` : ''}`).join('');
    return `<!DOCTYPE html><html lang="uk"><head><meta charset="utf-8"/>
      <title>Калькуляція: ${name}</title>
      <style>
        * { margin:0; padding:0; box-sizing:border-box; }
        body { font-family: Arial, sans-serif; font-size: 13px; color: #222; padding: 24px; }
        h1 { font-size:20px; color:#2c3e50; margin-bottom:4px; }
        h2 { font-size:15px; color:#2c3e50; margin:16px 0 8px; border-bottom:1px solid #ccc; padding-bottom:4px; }
        table { width:100%; border-collapse:collapse; margin-bottom:12px; }
        thead tr { background:#455a64; color:white; }
        thead th { padding:7px 8px; text-align:left; font-size:12px; }
        tfoot tr { background:#ecf0f1; font-weight:700; }
        tfoot td { padding:7px 8px; }
        .meta { display:flex; gap:24px; margin:8px 0 16px; flex-wrap:wrap; }
        .meta span { font-size:12px; color:#555; }
        .meta strong { color:#222; }
        .summary { display:flex; gap:20px; background:#f8f9fa; border:1px solid #dfe6e9; border-radius:6px; padding:12px 16px; margin:8px 0; flex-wrap:wrap; }
        .footer { margin-top:20px; font-size:11px; color:#999; border-top:1px solid #eee; padding-top:8px; display:flex; justify-content:space-between; }
        @media print { body { padding:12px; print-color-adjust:exact; -webkit-print-color-adjust:exact; } body * { print-color-adjust:exact; -webkit-print-color-adjust:exact; } }
      </style></head><body>
      <h1>Калькуляційна картка напівфабрикату</h1>
      <h2 style="border:none;font-size:18px;color:#222;">${name}</h2>
      <div class="meta">
        ${syncCode ? `<span>Код синхронізації: <strong>${syncCode}</strong></span>` : ''}
        ${catName ? `<span>Категорія: <strong>${catName}</strong></span>` : ''}
        ${weightType ? `<span>Одиниця: <strong>${weightType}</strong></span>` : ''}
        <span>Час приготування: <strong>${formatDurationLabel(cookingTime)}</strong></span>
        <span>Час викладки: <strong>${formatDurationLabel(serveTime)}</strong></span>
        <span>Вихід фактичний: <strong>${factualOut.toFixed(3)}</strong></span>
        <span>Алергени: <strong>${allergens.length > 0 ? allergens.join(' · ') : '—'}</strong></span>
        ${description ? `<span>Опис: <strong>${description}</strong></span>` : ''}
      </div>
      <h2>Склад напівфабрикату</h2>
      <table>
        <thead><tr>
          <th style="width:36px;">#</th><th>Назва інгредієнта</th>
          <th style="width:72px;text-align:right;">Брутто</th>
          <th style="width:72px;text-align:right;">Нетто</th>
          <th style="width:72px;text-align:right;">Вихід</th>
          <th style="width:72px;text-align:right;">Втрати %</th>
          <th style="width:80px;text-align:right;">Ціна/од</th>
          <th style="width:90px;text-align:right;">Вартість</th>
        </tr></thead>
        <tbody>${rowsHtml}</tbody>
        <tfoot><tr>
          <td colspan="2" style="text-align:right;">Σ Разом:</td>
          <td style="text-align:right;">${tBrutto.toFixed(3)}</td>
          <td style="text-align:right;">${tNetto.toFixed(3)}</td>
          <td style="text-align:right;">${tOutput.toFixed(3)}</td>
          <td style="text-align:right;color:#d35400;">${(tBrutto > 0 ? ((tBrutto - tOutput) / tBrutto) * 100 : 0).toFixed(2)}%</td>
          <td></td>
          <td style="text-align:right;color:#1a7a3c;">${totalCost.toFixed(2)} ₴</td>
        </tr></tfoot>
      </table>
      <div class="summary">
        <span>Брутто: <strong>${tBrutto.toFixed(3)}</strong></span>
        <span>Нетто: <strong>${tNetto.toFixed(3)}</strong></span>
        <span>Прогнозований вихід: <strong>${tOutput.toFixed(3)}</strong></span>
        <span>Вихід фактичний: <strong>${factualOut.toFixed(3)}</strong></span>
        <span>Собівартість: <strong>${totalCost.toFixed(2)} ₴</strong></span>
      </div>
      <div class="footer">
        <span>CRM4Chef — Система управління рестораном</span>
        <span>Дата: ${new Date().toLocaleDateString('uk-UA')}</span>
      </div>
      </body></html>`;
  };

  const getProductAccountingLabel = (productId: number | null, fallbackName: string | null) => {
    const product = productId != null ? products.find((p) => p.id === productId) : null;
    const labelName = product?.b52Name?.trim() || fallbackName || '—';
    const code = product?.b52Code?.trim() || '';
    return code ? `${code} — ${labelName}` : labelName;
  };

  const getSemiAccountingLabel = (semiId: number | null, fallbackName: string | null) => {
    const item = semiId != null ? items.find((s) => s.id === semiId) : null;
    const labelName = item?.b52Name?.trim() || fallbackName || '—';
    const code = item?.b52Code?.trim() || '';
    return code ? `${code} — ${labelName}` : labelName;
  };

  const buildSemiPrintDocument = (sf: SemiFinishedProduct, forAccounting = false, portions = 1) => {
    const safePortions = Math.max(1, portions);
    const ingRows = sf.items.map((i) => {
      const unitPrice = i.productId != null
        ? (products.find(p => p.id === i.productId)?.price ?? 0)
        : getSFPrice(i.subSemiFinishedProductId ?? 0);
      const brutto = Number(i.bruttoWeight) || Number(i.quantity) || 0;
      const netto = Number(i.nettoWeight) || 0;
      const output = Number(i.output) || 0;
      const rowLoss = brutto > 0 ? ((brutto - output) / brutto) * 100 : 0;
      return {
        label: forAccounting
          ? (i.productId != null
              ? getProductAccountingLabel(i.productId, i.productName)
              : getSemiAccountingLabel(i.subSemiFinishedProductId, i.subSemiFinishedProductName))
          : `${i.productName ?? i.subSemiFinishedProductName ?? '—'}`,
        note: i.notes?.trim() || '',
        brutto: brutto * safePortions,
        netto: netto * safePortions,
        output: output * safePortions,
        rowLoss,
        unitPrice,
        rowCost: unitPrice * brutto * safePortions,
      };
    });
    const tBrutto = ingRows.reduce((s, r) => s + r.brutto, 0);
    const tNetto = ingRows.reduce((s, r) => s + r.netto, 0);
    const tOutput = ingRows.reduce((s, r) => s + r.output, 0);
    return buildPrintHtml(
      forAccounting ? (sf.b52Name?.trim() || sf.name) : sf.name,
      forAccounting ? (sf.b52Code?.trim() || '') : '',
      sf.categoryName,
      sf.weightType,
      sf.cookingTime,
      sf.serveTime,
      sf.description,
      ingRows,
      tBrutto,
      tNetto,
      tOutput,
      sf.costPrice,
      (sf.isAuxiliaryRecipe ? 1 : sf.netWeight) * safePortions,
      getAllergenBadges(getSemiComputedAllergens(sf)),
      safePortions
    );
  };

  const buildBatchPrintHtml = (title: string, docs: string[]) => {
    const styleMatch = docs[0]?.match(/<style>([\s\S]*?)<\/style>/i);
    const styleContent = styleMatch ? styleMatch[1] : 'body { font-family: Arial, sans-serif; font-size: 13px; color: #222; padding: 18px; }';
    const pages = docs
      .map((doc) => doc.match(/<body>([\s\S]*?)<\/body>/i)?.[1] ?? doc)
      .join('<div style="page-break-after: always;"></div>');

    return `<!DOCTYPE html><html lang="uk"><head><meta charset="utf-8"/><title>${title}</title><style>${styleContent}</style></head><body>${pages}</body></html>`;
  };

  const handlePrintSemi = (sf: SemiFinishedProduct, forAccounting = false) => {
    const portions = Math.max(1, parseFloat(printPortions.replace(',', '.')) || 1);
    openPrintWindow(buildSemiPrintDocument(sf, forAccounting, portions));
  };

  const handlePrintCheatSheets = () => {
    if (selectedIds.length === 0) return;
    const selected = selectedIds
      .map((id) => items.find((sf) => sf.id === id))
      .filter((sf): sf is SemiFinishedProduct => sf != null);

    const escH = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const unitLabel = (wt: string) => WEIGHT_TYPES.find((w) => w.value === wt)?.label ?? wt;

    const cards = selected.map((sf) => {
      const rows = sf.items.map((i) => {
        const name = i.productName ?? i.subSemiFinishedProductName ?? '—';
        const brutto = Number(i.bruttoWeight) || Number(i.quantity) || 0;
        return `<tr><td>${escH(name)}</td><td style="text-align:right;white-space:nowrap;">${brutto.toFixed(3)} ${escH(unitLabel(sf.weightType))}</td></tr>`;
      }).join('');
      return `<div class="card">
        <div class="card-title">${escH(sf.name)}</div>
        <div class="card-out">Вихід: <strong>${sf.netWeight.toFixed(3)} ${escH(unitLabel(sf.weightType))}</strong></div>
        <table><tbody>${rows}</tbody></table>
      </div>`;
    }).join('');

    const html = `<!DOCTYPE html><html lang="uk"><head><meta charset="utf-8"/>
      <title>Шпаргалки Н/Ф</title>
      <style>
        @page { margin: 10mm; }
        body { font-family: Arial, sans-serif; font-size: 11px; color: #1a1a1a; margin: 0; }
        .grid { display: flex; flex-wrap: wrap; gap: 8px; }
        .card { border: 1px solid #555; border-radius: 4px; padding: 6px 8px; width: 200px; page-break-inside: avoid; break-inside: avoid; }
        .card-title { font-size: 12px; font-weight: bold; border-bottom: 1px solid #ccc; margin-bottom: 4px; padding-bottom: 3px; line-height: 1.3; }
        .card-out { font-size: 11px; margin-bottom: 4px; color: #333; }
        table { width: 100%; border-collapse: collapse; }
        td { padding: 2px 3px; font-size: 11px; border-bottom: 1px solid #eee; vertical-align: top; }
        td:last-child { width: 70px; }
        @media print { .grid { display: flex; flex-wrap: wrap; } }
      </style></head><body>
      <div class="grid">${cards}</div>
      </body></html>`;

    void openAndPrintHtml(html, {
      windowFeatures: 'width=950,height=780',
      minimumDelayMs: 500,
      onPopupBlocked: () => alert('Дозвольте спливаючі вікна для друку'),
    });
  };

  const resolvePrintIds = (manualIds: number[], categoryIds: number[]) => {
    const byCategory = categoryIds.length === 0
      ? []
      : items.filter((sf) => sf.categoryId != null && categoryIds.includes(sf.categoryId)).map((sf) => sf.id);
    return Array.from(new Set([...manualIds, ...byCategory])).filter((id) => items.some((sf) => sf.id === id));
  };

  const handlePrintSelectedBatch = (manualIds: number[], categoryIds: number[], forAccounting = false) => {
    const ids = resolvePrintIds(manualIds, categoryIds);
    if (ids.length === 0) {
      setError('Не обрано жодної позиції для друку.');
      return;
    }
    const docs = ids
      .map((id) => items.find((sf) => sf.id === id))
      .filter((sf): sf is SemiFinishedProduct => sf != null)
      .map((sf) => buildSemiPrintDocument(sf, forAccounting));
    openPrintWindow(buildBatchPrintHtml(forAccounting ? 'Бухгалтерський друк Н/Ф' : 'Друк Н/Ф', docs));
  };

  const handlePrintModal = () => {
    const ingRows = rows.filter(r => r.refId !== '').map((r) => {
      const unitPrice = r.type === 'product'
        ? (products.find(p => p.id === parseInt(r.refId))?.price ?? 0)
        : getSFPrice(parseInt(r.refId));
      const label = r.type === 'product'
        ? (products.find(p => p.id === parseInt(r.refId))?.name ?? '—')
        : (items.find(s => s.id === parseInt(r.refId))?.name ?? '—');
      const brutto = parseFloat(r.bruttoWeight) || 0;
      const netto = parseFloat(r.nettoWeight) || 0;
      const output = parseFloat(r.output) || 0;
      return {
        label: `${label}`,
        note: r.notes.trim(),
        brutto,
        netto,
        output,
        rowLoss: brutto > 0 ? ((brutto - output) / brutto) * 100 : 0,
        unitPrice,
        rowCost: unitPrice * brutto,
      };
    });
    const catName = form.categoryId ? (categories.find(c => c.id === parseInt(form.categoryId))?.name ?? null) : null;
    const computedAllergens = rows
      .filter(r => r.refId !== '')
      .flatMap((r) => {
        if (r.type === 'product') return products.find(p => p.id === parseInt(r.refId))?.allergens ?? [];
        return items.find(s => s.id === parseInt(r.refId))?.allergens ?? [];
      })
      .filter((a, idx, arr) => arr.findIndex(x => x.id === a.id) === idx);
    openPrintWindow(buildPrintHtml(
      form.name || 'Новий напівфабрикат',
      form.b52Code || '',
      catName,
      form.weightType,
      parseDurationToSeconds(form.cookingTime),
      parseDurationToSeconds(form.serveTime),
      form.description,
      ingRows,
      totalBrutto,
      totalNetto,
      totalOutput,
      calcCost(),
      factualOutput,
      getAllergenBadges(computedAllergens)
    ));
  };

  const handlePrintAccountingModal = () => {
    const ingRows = rows.filter(r => r.refId !== '').map((r) => {
      const unitPrice = r.type === 'product'
        ? (products.find(p => p.id === parseInt(r.refId))?.price ?? 0)
        : getSFPrice(parseInt(r.refId));
      const brutto = parseFloat(r.bruttoWeight) || 0;
      const netto = parseFloat(r.nettoWeight) || 0;
      const output = parseFloat(r.output) || 0;
      return {
        label: r.type === 'product'
          ? getProductAccountingLabel(parseInt(r.refId), products.find(p => p.id === parseInt(r.refId))?.name ?? '—')
          : getSemiAccountingLabel(parseInt(r.refId), items.find(s => s.id === parseInt(r.refId))?.name ?? '—'),
        note: r.notes.trim(),
        brutto,
        netto,
        output,
        rowLoss: brutto > 0 ? ((brutto - output) / brutto) * 100 : 0,
        unitPrice,
        rowCost: unitPrice * brutto,
      };
    });
    const catName = form.categoryId ? (categories.find(c => c.id === parseInt(form.categoryId))?.name ?? null) : null;
    const computedAllergens = rows
      .filter(r => r.refId !== '')
      .flatMap((r) => {
        if (r.type === 'product') return products.find(p => p.id === parseInt(r.refId))?.allergens ?? [];
        return items.find(s => s.id === parseInt(r.refId))?.allergens ?? [];
      })
      .filter((a, idx, arr) => arr.findIndex(x => x.id === a.id) === idx);
    openPrintWindow(buildPrintHtml(
      form.b52Name || form.name || 'Новий напівфабрикат',
      form.b52Code || '',
      catName,
      form.weightType,
      parseDurationToSeconds(form.cookingTime),
      parseDurationToSeconds(form.serveTime),
      form.description,
      ingRows,
      totalBrutto,
      totalNetto,
      totalOutput,
      calcCost(),
      factualOutput,
      getAllergenBadges(computedAllergens)
    ));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError("Назва є обов'язковою"); return; }
    if (saveArchiveSnapshot && !archiveEffectiveFromUtc) { setError('Вкажіть дату архівної копії.'); return; }
    const finalOutput = parseFloat(form.finalOutput);
    if (Number.isNaN(finalOutput) || finalOutput < 0) { setError("Вкажіть коректний вихід напівфабрикату"); return; }
    setSaving(true); setError(null);
    const payload = {
      name: form.name.trim(),
      b52Code: form.b52Code.trim(),
      b52Name: form.b52Name.trim(),
      categoryId: form.categoryId ? parseInt(form.categoryId) : null,
      cookingTime: parseDurationToSeconds(form.cookingTime),
      serveTime: parseDurationToSeconds(form.serveTime),
      weightType: form.weightType,
      description: form.description,
      excludeFromDishWeightCalc: form.excludeFromDishWeightCalc,
      isAuxiliaryRecipe: form.isAuxiliaryRecipe,
      preparationSteps: form.preparationSteps,
      saveArchiveSnapshot,
      archiveEffectiveFromUtc: saveArchiveSnapshot ? archiveEffectiveFromUtc || new Date().toISOString().slice(0, 10) : null,
      // Вихід напівфабрикату (факт) вводиться вручну
      netWeight: finalOutput,
      grossWeight: totalBrutto,
      items: rows.filter(r => r.refId !== '').map(r => ({
        productId: r.type === 'product' ? parseInt(r.refId) : null,
        subSemiFinishedProductId: r.type === 'semi' ? parseInt(r.refId) : null,
        // Склад = сума Брутто інгредієнтів
        quantity: parseFloat(r.bruttoWeight) || 0,
        bruttoWeight: parseFloat(r.bruttoWeight) || 0,
        nettoWeight: parseFloat(r.nettoWeight) || 0,
        output: parseFloat(r.output) || 0,
        notes: (r.notes ?? '').trim(),
        operationNotes: (r.operationNotes ?? '').trim(),
        preparationOperationId: r.preparationOperationId ? parseInt(r.preparationOperationId) : null,
        processingMethodId: r.processingMethodId ? parseInt(r.processingMethodId) : null,
        processingTemperatureC: r.processingTemperatureC.trim() ? parseFloat(r.processingTemperatureC) || null : null,
      })),
    };
    try {
      if (editingId) await dispatch(updateSemiFinished({ id: editingId, data: payload })).unwrap();
      else await dispatch(createSemiFinished(payload)).unwrap();
      await dispatch(fetchSemiFinished()).unwrap();
      closeModal();
    } catch (err: any) {
      setError(extractApiErrorMessage(err, 'Не вдалося зберегти напівфабрикат.'));
    } finally {
      setSaving(false);
    }
  };

  const uploadPreparationStepPhoto = async (stepIndex: number, file: File) => {
    if (!editingId) throw new Error('Спочатку збережіть напівфабрикат.');
    const body = new FormData();
    body.append('file', file);
    const response = await api.post<{ url: string }>(
      `/recipe-preparation-steps/semi-finished/${editingId}/${stepIndex}/photo`,
      body,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.url;
  };

  const handleDelete = async (id: number) => {
    const item = items.find((row) => row.id === id);
    const usageEntries = getSemiUsageEntries(id);
    if (usageEntries.length > 0) {
      setDeleteBlockModal({
        title: `Неможливо видалити Н/Ф "${item?.name ?? `ID ${id}`}"`,
        message: 'Спроба видалити Н/Ф, який використовується в інших сутностях.',
        entries: usageEntries,
        semiFinishedId: id,
      });
      return;
    }
    if (!window.confirm('Видалити напівфабрикат?')) return;
    try {
      setError(null);
      await dispatch(deleteSemiFinished(id)).unwrap();
      setSelectedIds((prev) => prev.filter((x) => x !== id));
    } catch (err: any) {
      const message = extractApiErrorMessage(err, 'Не вдалося видалити напівфабрикат');
      setDeleteBlockModal({
        title: `Неможливо видалити Н/Ф "${item?.name ?? `ID ${id}`}"`,
        message,
        entries: usageEntries,
        semiFinishedId: id,
      });
      setError(message);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      setError('Спочатку виберіть напівфабрикати для видалення.');
      return;
    }
    if (!window.confirm(`Видалити вибрані напівфабрикати (${selectedIds.length})?`)) return;

    const blockedLocal = selectedIds
      .map((id) => ({ id, item: items.find((row) => row.id === id), entries: getSemiUsageEntries(id) }))
      .find((row) => row.entries.length > 0);
    if (blockedLocal) {
      setDeleteBlockModal({
        title: `Неможливо видалити Н/Ф "${blockedLocal.item?.name ?? `ID ${blockedLocal.id}`}"`,
        message: 'Спроба масового видалення. Н/Ф входить у страви/інші Н-Ф і не може бути видалений.',
        entries: blockedLocal.entries,
        semiFinishedId: blockedLocal.id,
      });
      return;
    }

    try {
      setError(null);
      const result = await dispatch(bulkDeleteSemiFinished(selectedIds)).unwrap();
      setSelectedIds((prev) => prev.filter((id) => !result.deletedIds.includes(id)));
      if (result.errors.length > 0) {
        const blockedId = selectedIds.find((id) => !result.deletedIds.includes(id));
        if (blockedId != null) {
          const blockedItem = items.find((row) => row.id === blockedId);
          setDeleteBlockModal({
            title: `Неможливо видалити Н/Ф "${blockedItem?.name ?? `ID ${blockedId}`}"`,
            message: result.errors.join(' '),
            entries: getSemiUsageEntries(blockedId),
            semiFinishedId: blockedId,
          });
        }
        setError(result.errors.join(' '));
      }
    } catch (err: any) {
      const message = extractApiErrorMessage(err, 'Не вдалося видалити вибрані напівфабрикати');
      const blockedId = selectedIds[0];
      const blockedItem = blockedId != null ? items.find((row) => row.id === blockedId) : null;
      setDeleteBlockModal({
        title: `Неможливо видалити Н/Ф "${blockedItem?.name ?? 'позиція'}"`,
        message,
        entries: blockedId != null ? getSemiUsageEntries(blockedId) : [],
        semiFinishedId: blockedId,
      });
      setError(message);
    }
  };

  const handleRecalculateCosts = async () => {
    if (!window.confirm('Перерахувати собівартість для всіх Н/Ф і страв?')) return;

    try {
      setError(null);
      setImportMsg(null);
      setRecalculatingCosts(true);
      const result = await dispatch(recalculateDishAndSemiCosts()).unwrap();
      await dispatch(fetchSemiFinished());
      await dispatch(fetchDishes());
      setImportMsg(`Собівартість перераховано. Н/Ф: ${result.semiFinishedUpdated}, страв: ${result.dishesUpdated}.`);
    } catch (err: any) {
      setError(extractApiErrorMessage(err, 'Не вдалося перерахувати собівартість Н/Ф та страв'));
    } finally {
      setRecalculatingCosts(false);
    }
  };

  const buildDishPayload = (dish: Dish, ingredients = dish.ingredients) => ({
    name: dish.name,
    b52Code: dish.b52Code ?? '',
    b52Name: dish.b52Name ?? '',
    categoryId: dish.categoryId,
    description: dish.description,
    cookingTime: dish.cookingTime,
    serveTime: dish.serveTime ?? 0,
    technologyDescription: dish.technologyDescription,
    inventoryRequired: dish.inventoryRequired,
    photoUrl: dish.photoUrl,
    netWeight: dish.netWeight,
    grossWeight: dish.grossWeight,
    cleanOutput: dish.cleanOutput,
    marginType: dish.marginType,
    margin: dish.margin,
    ingredients: ingredients.map((ingredient) => ({
      productId: ingredient.productId,
      semiFinishedProductId: ingredient.semiFinishedProductId,
      quantity: ingredient.quantity,
      bruttoWeight: ingredient.bruttoWeight,
      nettoWeight: ingredient.nettoWeight,
      output: ingredient.output,
      notes: ingredient.notes ?? '',
      operationNotes: ingredient.operationNotes ?? '',
      preparationOperationId: ingredient.preparationOperationId ?? null,
      processingMethodId: ingredient.processingMethodId ?? null,
      processingTemperatureC: ingredient.processingTemperatureC ?? null,
    })),
  });

  const buildSemiFinishedPayload = (
    item: SemiFinishedProduct,
    rows: Array<{
      productId: number | null;
      subSemiFinishedProductId: number | null;
      quantity: number;
      bruttoWeight: number;
      nettoWeight: number;
      output: number;
      notes?: string;
      operationNotes?: string | null;
      preparationOperationId?: number | null;
      processingMethodId?: number | null;
      processingTemperatureC?: number | null;
    }> = item.items
  ) => ({
    name: item.name,
    b52Code: item.b52Code ?? '',
    b52Name: item.b52Name ?? '',
    categoryId: item.categoryId,
    description: item.description,
    cookingTime: item.cookingTime,
    serveTime: item.serveTime,
    weightType: item.weightType,
    netWeight: item.netWeight,
    grossWeight: item.grossWeight,
    excludeFromDishWeightCalc: item.excludeFromDishWeightCalc,
    isAuxiliaryRecipe: item.isAuxiliaryRecipe ?? false,
    preparationSteps: item.preparationSteps ?? [],
    items: rows.map((row) => ({
      productId: row.productId,
      subSemiFinishedProductId: row.subSemiFinishedProductId,
      quantity: row.quantity,
      bruttoWeight: row.bruttoWeight,
      nettoWeight: row.nettoWeight,
      output: row.output,
      notes: row.notes ?? '',
      operationNotes: row.operationNotes ?? '',
      preparationOperationId: row.preparationOperationId ?? null,
      processingMethodId: row.processingMethodId ?? null,
      processingTemperatureC: row.processingTemperatureC ?? null,
    })),
  });

  const getSemiUsageEntries = (semiId: number): UsageEntry[] => {
    const getUsedQuantity = (bruttoWeight: number, quantity: number) => {
      if (Number.isFinite(bruttoWeight) && bruttoWeight > 0) return bruttoWeight;
      if (Number.isFinite(quantity) && quantity > 0) return quantity;
      return 0;
    };

    const semiEntries = items
      .map((item) => ({
        kind: 'semi' as const,
        id: item.id,
        name: item.name,
        quantity: item.items
          .filter((row) => row.subSemiFinishedProductId === semiId)
          .reduce((sum, row) => sum + getUsedQuantity(row.bruttoWeight, row.quantity), 0),
      }))
      .filter((entry) => entry.quantity > 0);

    const dishEntries = dishes
      .map((dish) => ({
        kind: 'dish' as const,
        id: dish.id,
        name: dish.name,
        quantity: dish.ingredients
          .filter((ingredient) => ingredient.semiFinishedProductId === semiId)
          .reduce((sum, ingredient) => sum + getUsedQuantity(ingredient.bruttoWeight, ingredient.quantity), 0),
      }))
      .filter((entry) => entry.quantity > 0);

    return [...dishEntries, ...semiEntries].sort((a, b) => {
      if (a.kind !== b.kind) return a.kind === 'dish' ? -1 : 1;
      return a.name.localeCompare(b.name, 'uk');
    });
  };

  const openDetailsModal = (item: SemiFinishedProduct) => {
    if (!manualProduction.isCurrentScope()) return;
    const request = ++detailsRequest.current;
    const requestedScope = localStorage.getItem(OPERATING_GROUP_STORAGE_KEY);
    const isCurrent = () => request === detailsRequest.current && manualProduction.isCurrentScope()
      && localStorage.getItem(OPERATING_GROUP_STORAGE_KEY) === requestedScope;
    setDetailsPortions('1');
    setDetailsWorkStart('');
    setDetailsWorkEnd('');
    setDetailsWorkPortions('');
    setProductionFormOpen(false);
    setProductionQuantity('');
    setProductionDate(new Date().toISOString().slice(0, 10));
    setProductionNotes('');
    setProductionError(null);
    setDetailsModal({
      semi: item,
      usageEntries: getSemiUsageEntries(item.id),
      usageReportEntries: [],
      productionEntries: [],
      reportLoading: true,
      reportError: null,
      activeReportTab: 'usage-report',
    });
    const from = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    const to = new Date().toISOString().slice(0, 10);
    Promise.all([
      api.get<SemiFinishedUsageReportEntry[]>(`/semifinishedproducts/${item.id}/usage-report`, { params: { from, to } }),
      api.get<SemiFinishedProduction[]>(`/semifinishedproductions`, { params: { semiFinishedProductId: item.id, from, to: `${to}T23:59:59` } }),
    ]).then(([usage, production]) => {
      if (!isCurrent()) return;
      setDetailsModal((prev) => prev && prev.semi.id === item.id
        ? { ...prev, usageReportEntries: usage.data ?? [], productionEntries: production.data ?? [], reportLoading: false }
        : prev);
    }).catch((err: any) => {
      if (!isCurrent()) return;
      setDetailsModal((prev) => prev && prev.semi.id === item.id
        ? { ...prev, reportLoading: false, reportError: err?.response?.data?.message ?? 'Не вдалося завантажити звіти Н/Ф.' }
        : prev);
    });
  };

  const createProduction = async () => {
    if (!detailsModal || productionSaving || manualProduction.blocked || !manualProduction.isCurrentScope()) return;
    const pending = manualProduction.operationFor(detailsModal.semi.id);
    const quantity = pending?.payload.quantity ?? Number(productionQuantity);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setProductionError('Вкажіть додатну кількість виробленого Н/Ф.');
      return;
    }
    if (!(pending?.payload.date ?? productionDate)) {
      setProductionError('Вкажіть дату виробництва.');
      return;
    }

    const productId = detailsModal.semi.id;
    const request = ++detailsRequest.current;
    const isCurrent = () => request === detailsRequest.current && manualProduction.isCurrentScope();
    const saved = await manualProduction.submit(pending?.payload ?? {
      semiFinishedProductId: productId,
      quantity,
      date: productionDate,
      notes: productionNotes.trim() || null,
    }, async () => {
      const to = new Date().toISOString().slice(0, 10);
      const from = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      const production = await api.get<SemiFinishedProduction[]>('/semifinishedproductions', {
        params: { semiFinishedProductId: productId, from, to: `${to}T23:59:59` },
      });
      if (!isCurrent()) return;
      setDetailsModal((prev) => prev && prev.semi.id === productId
        ? { ...prev, productionEntries: production.data ?? [], reportLoading: false }
        : prev);
    });
    if (saved && isCurrent()) {
      setProductionQuantity('');
      setProductionNotes('');
      setProductionFormOpen(false);
      setProductionDate(new Date().toISOString().slice(0, 10));
    }
  };

  const printSemiDetailsModal = (modal: SemiDetailsModalState) => {
    const escapeHtml = (value: string) =>
      value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const unitLabel = WEIGHT_TYPES.find((w) => w.value === modal.semi.weightType)?.label ?? modal.semi.weightType;
    const allergensText = getSemiComputedAllergens(modal.semi).length > 0
      ? getSemiComputedAllergens(modal.semi).map((a) => `${getAllergenIcon(a.name)} ${a.name}`).join(', ')
      : '—';
    const usageRowsHtml = modal.usageEntries.length === 0
      ? '<tr><td colspan="4" style="color:#7f8c8d;">Немає входжень у стравах або напівфабрикатах.</td></tr>'
      : modal.usageEntries.map((entry) => `
          <tr>
            <td>${entry.kind === 'dish' ? 'Страва' : 'Н/Ф'}</td>
            <td>${entry.id}</td>
            <td>${escapeHtml(entry.name)}</td>
            <td style="text-align:right;">${entry.quantity.toFixed(4)}</td>
          </tr>
        `).join('');

    const html = `<!DOCTYPE html><html lang="uk"><head><meta charset="utf-8"/>
      <title>Деталізація Н/Ф: ${escapeHtml(modal.semi.name)}</title>
      <style>
        body{font-family:Arial,sans-serif;padding:20px;color:#1f2937;}
        h1,h2{margin:0 0 10px;}
        .meta{font-size:13px;color:#34495e;margin:2px 0;}
        table{width:100%;border-collapse:collapse;margin:10px 0 16px;}
        th,td{border:1px solid #d1d5db;padding:7px 8px;font-size:12px;vertical-align:top;}
        th{background:#ecf0f1;text-align:left;}
      </style></head><body>
      <h1>Н/Ф: ${escapeHtml(modal.semi.name)}</h1>
      <div class="meta">Категорія: <strong>${escapeHtml(modal.semi.categoryName || '—')}</strong></div>
      <div class="meta">Одиниця: <strong>${escapeHtml(unitLabel)}</strong></div>
      <div class="meta">Час приготування: <strong>${escapeHtml(formatDurationLabel(modal.semi.cookingTime))}</strong></div>
      <div class="meta">Час викладки: <strong>${escapeHtml(formatDurationLabel(modal.semi.serveTime))}</strong></div>
      <div class="meta">Брутто: <strong>${modal.semi.grossWeight.toFixed(3)}</strong></div>
      <div class="meta">Нетто/вихід: <strong>${modal.semi.netWeight.toFixed(3)}</strong></div>
      <div class="meta">Алергени: <strong>${escapeHtml(allergensText)}</strong></div>
      ${modal.semi.description ? `<div class="meta">Опис: <strong>${escapeHtml(modal.semi.description)}</strong></div>` : ''}
      <h2>Входження у стравах та Н/Ф</h2>
      <table>
        <thead><tr><th>Тип</th><th>ID</th><th>Назва</th><th>Кількість продукту/н/ф</th></tr></thead>
        <tbody>${usageRowsHtml}</tbody>
      </table>
      </body></html>`;

    openPrintWindow(html);
  };

  const openUsageModal = (item: SemiFinishedProduct) => {
    setUsageModal({
      title: `Входження: ${item.name}`,
      entries: getSemiUsageEntries(item.id),
    });
  };

  const openReplaceModal = (item: SemiFinishedProduct) => {
    setReplaceSource(item);
    setReplaceTargetId('');
    setError(null);
  };

  const closeReplaceModal = () => {
    setReplaceSource(null);
    setReplaceTargetId('');
    setReplacing(false);
  };

  const executeSemiFinishedReplacement = async (source: SemiFinishedProduct, target: SemiFinishedProduct) => {
    const affectedSemiFinished = items.filter((item) => item.items.some((row) => row.subSemiFinishedProductId === source.id));
    const affectedDishes = dishes.filter((dish) => dish.ingredients.some((ingredient) => ingredient.semiFinishedProductId === source.id));
    const totalMatches = affectedSemiFinished.length + affectedDishes.length;

    if (affectedSemiFinished.some((item) => item.id === target.id)) {
      throw new Error('Не можна виконати заміну: цільовий напівфабрикат вже містить цей Н/Ф у своєму складі.');
    }

    for (const item of affectedSemiFinished) {
      const nextRows = item.items.map((row) => row.subSemiFinishedProductId === source.id
        ? { ...row, subSemiFinishedProductId: target.id }
        : row);
      await dispatch(updateSemiFinished({ id: item.id, data: buildSemiFinishedPayload(item, nextRows) })).unwrap();
    }

    for (const dish of affectedDishes) {
      const nextIngredients = dish.ingredients.map((ingredient) => ingredient.semiFinishedProductId === source.id
        ? { ...ingredient, semiFinishedProductId: target.id }
        : ingredient);
      await dispatch(updateDish({ id: dish.id, data: buildDishPayload(dish, nextIngredients) })).unwrap();
    }

    await Promise.all([
      dispatch(fetchSemiFinished()),
      dispatch(fetchDishes()),
    ]);

    return { totalMatches };
  };

  const handleReplaceSemiFinished = async () => {
    if (!replaceSource) return;
    const targetIdNum = parseInt(replaceTargetId, 10);
    if (!targetIdNum) {
      setError('Оберіть напівфабрикат, на який потрібно замінити.');
      return;
    }

    const target = items.find((item) => item.id === targetIdNum);
    if (!target) {
      setError('Напівфабрикат для заміни не знайдено.');
      return;
    }

    const usageCount = getSemiUsageEntries(replaceSource.id).length;
    if (!window.confirm(`Замінити "${replaceSource.name}" на "${target.name}" у всіх стравах та напівфабрикатах${usageCount > 0 ? ` (${usageCount} входжень)` : ''}?`)) return;

    try {
      setReplacing(true);
      setError(null);
      const { totalMatches } = await executeSemiFinishedReplacement(replaceSource, target);
      if (totalMatches === 0) {
        setError(`Напівфабрикат "${replaceSource.name}" ніде не використовується.`);
        setReplacing(false);
        return;
      }
      closeReplaceModal();
    } catch (err: any) {
      setError(extractApiErrorMessage(err, 'Не вдалося виконати масову заміну напівфабрикату'));
      setReplacing(false);
    }
  };

  const handleReplaceFromDeleteModal = async () => {
    if (!deleteBlockModal?.semiFinishedId) return;
    const source = items.find((item) => item.id === deleteBlockModal.semiFinishedId);
    if (!source) {
      setError('Напівфабрикат для заміни не знайдено.');
      return;
    }

    const targetIdNum = parseInt(deleteReplaceTargetId, 10);
    if (!targetIdNum) {
      setError('Оберіть напівфабрикат, на який потрібно замінити.');
      return;
    }

    const target = items.find((item) => item.id === targetIdNum);
    if (!target) {
      setError('Напівфабрикат для заміни не знайдено.');
      return;
    }

    try {
      setReplacing(true);
      setError(null);
      await executeSemiFinishedReplacement(source, target);
      await dispatch(deleteSemiFinished(source.id)).unwrap();
      setSelectedIds((prev) => prev.filter((x) => x !== source.id));
      setDeleteBlockModal(null);
      setDeleteReplaceTargetId('');
      window.alert(`Н/Ф "${source.name}" замінено на "${target.name}" і видалено.`);
    } catch (err: any) {
      const message = extractApiErrorMessage(err, 'Не вдалося замінити та видалити напівфабрикат');
      setDeleteBlockModal({
        title: `Неможливо видалити Н/Ф "${source.name}"`,
        message,
        entries: getSemiUsageEntries(source.id),
        semiFinishedId: source.id,
      });
      setError(message);
      setReplacing(false);
    } finally {
      setReplacing(false);
    }
  };

  const normalizeLookup = (value: unknown) => String(value ?? '').trim().toLowerCase();

  const parseFlag = (value: unknown, fallback = false) => {
    const normalized = normalizeLookup(value);
    if (!normalized) return fallback;
    return ['так', 'true', '1', 'yes', 'y'].includes(normalized);
  };

  const resolveSemiFinishedByRow = (pool: SemiFinishedProduct[], row: any) => {
    const id = parseInt(String(row['Код (ID)'] ?? row['id'] ?? row['Н/Ф ID'] ?? '').trim(), 10);
    const name = String(row['Назва'] ?? row['Н/Ф'] ?? '').trim();
    const b52Code = String(row['Код B52'] ?? row['b52Code'] ?? row['Код B52 Н/Ф'] ?? '').trim();

    if (b52Code) return uniqueRecipeMatch(pool, item => normalizeLookup(item.b52Code) === normalizeLookup(b52Code));
    if (Number.isInteger(id) && id > 0) return pool.find(item => item.id === id);
    return name ? uniqueRecipeMatch(pool, item => normalizeLookup(item.name) === normalizeLookup(name)) : undefined;
  };

  const resolveProductIngredient = (row: any) => {
    const id = parseInt(String(row['ID інгредієнта'] ?? row['ProductId'] ?? '').trim(), 10);
    const name = String(row['Назва інгредієнта'] ?? row['ProductName'] ?? '').trim();
    const b52Code = String(row['Код B52 інгредієнта'] ?? row['ProductB52Code'] ?? '').trim();
    const b52Name = String(row['Назва B52 інгредієнта'] ?? row['ProductB52Name'] ?? '').trim();

    if (b52Code) return uniqueRecipeMatch(products, product => normalizeLookup(product.b52Code) === normalizeLookup(b52Code));
    if (b52Name) return uniqueRecipeMatch(products, product => normalizeLookup(product.b52Name) === normalizeLookup(b52Name));
    if (Number.isInteger(id) && id > 0) return products.find(product => product.id === id);
    return name ? uniqueRecipeMatch(products, product => normalizeLookup(product.name) === normalizeLookup(name)) : undefined;
  };

  const resolveSemiIngredient = (pool: SemiFinishedProduct[], row: any) => {
    const id = parseInt(String(row['ID інгредієнта'] ?? row['SubSemiFinishedProductId'] ?? '').trim(), 10);
    const name = String(row['Назва інгредієнта'] ?? row['SubSemiFinishedProductName'] ?? '').trim();
    const b52Code = String(row['Код B52 інгредієнта'] ?? row['SubSemiFinishedB52Code'] ?? '').trim();
    const b52Name = String(row['Назва B52 інгредієнта'] ?? row['SubSemiFinishedB52Name'] ?? '').trim();

    if (b52Code) return uniqueRecipeMatch(pool, item => normalizeLookup(item.b52Code) === normalizeLookup(b52Code));
    if (b52Name) return uniqueRecipeMatch(pool, item => normalizeLookup(item.b52Name) === normalizeLookup(b52Name));
    if (Number.isInteger(id) && id > 0) return pool.find(item => item.id === id);
    return name ? uniqueRecipeMatch(pool, item => normalizeLookup(item.name) === normalizeLookup(name)) : undefined;
  };

  const resolvePreparationOperation = (row: any) => {
    const id = parseInt(String(row['Операція ID'] ?? '').trim(), 10);
    const name = String(row['Операція'] ?? '').trim();
    if (Number.isInteger(id) && id > 0) return preparationOperations.find(operation => operation.id === id);
    return name ? uniqueRecipeMatch(preparationOperations, operation => normalizeLookup(operation.name) === normalizeLookup(name)) : undefined;
  };

  const resolveProcessingMethod = (row: any) => {
    const id = parseInt(String(row['Метод обробки ID'] ?? '').trim(), 10);
    const name = String(row['Метод обробки'] ?? '').trim();
    if (Number.isInteger(id) && id > 0) return processingMethods.find(method => method.id === id);
    return name ? uniqueRecipeMatch(processingMethods, method => normalizeLookup(method.name) === normalizeLookup(name)) : undefined;
  };

  // ── Excel Export ────────────────────────────────────────────
  const handleExport = () => {
    const rows2 = items.map(sf => ({
      'Код (ID)': sf.id,
      'Назва': sf.name,
      'Код B52': sf.b52Code ?? '',
      'Назва B52': sf.b52Name ?? '',
      'Категорія': sf.categoryName ?? '',
      'Час приготування (сек або хв,сек)': sf.cookingTime ?? 0,
      'Час на видачу (сек або хв,сек)': sf.serveTime ?? 0,
      'Одиниця виміру': WEIGHT_TYPES.find(w => w.value === sf.weightType)?.label ?? sf.weightType,
      'Брутто': sf.grossWeight,
      'Нетто': sf.netWeight,
      'Собівартість (₴)': sf.costPrice,
      'Опис': sf.description ?? '',
      'Кроки приготування JSON': JSON.stringify(sf.preparationSteps ?? []),
      'Не рахувати в стравах': sf.excludeFromDishWeightCalc ? 'Так' : 'Ні',
      'Набір на порцію': sf.isAuxiliaryRecipe ? 'Так' : 'Ні',
      'Склад': sf.items.map(i => `${i.productName ?? i.subSemiFinishedProductName}(${i.output})${i.notes ? ` [Нотатка: ${i.notes}]` : ''}`).join('; '),
    }));
    const compositionRows = items.flatMap((sf) => sf.items.map((ingredient) => {
      const product = ingredient.productId != null ? products.find((p) => p.id === ingredient.productId) : null;
      const subSemi = ingredient.subSemiFinishedProductId != null ? items.find((item) => item.id === ingredient.subSemiFinishedProductId) : null;
      return {
        'Н/Ф ID': sf.id,
        'Н/Ф': sf.name,
        'Код B52 Н/Ф': sf.b52Code ?? '',
        'Назва B52 Н/Ф': sf.b52Name ?? '',
        'Тип інгредієнта': ingredient.productId != null ? 'Товар' : 'Н/Ф',
        'ID інгредієнта': ingredient.productId ?? ingredient.subSemiFinishedProductId ?? '',
        'Назва інгредієнта': ingredient.productName ?? ingredient.subSemiFinishedProductName ?? '',
        'Код B52 інгредієнта': product?.b52Code ?? subSemi?.b52Code ?? '',
        'Назва B52 інгредієнта': product?.b52Name ?? subSemi?.b52Name ?? '',
        'Кількість': ingredient.quantity,
        'Брутто': ingredient.bruttoWeight,
        'Нетто': ingredient.nettoWeight,
        'Вихід': ingredient.output,
        'Нотатка': ingredient.notes ?? '',
        'Операційна нотатка': ingredient.operationNotes ?? '',
        'Операція ID': ingredient.preparationOperationId ?? '',
        'Операція': ingredient.preparationOperationName ?? '',
        'Метод обробки ID': ingredient.processingMethodId ?? '',
        'Метод обробки': ingredient.processingMethodName ?? '',
        'Температура обробки °C': ingredient.processingTemperatureC ?? '',
      };
    }));
    const ws = XLSX.utils.json_to_sheet(rows2);
    ws['!cols'] = [{ wch: 8 }, { wch: 28 }, { wch: 16 }, { wch: 28 }, { wch: 22 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 16 }, { wch: 28 }, { wch: 18 }, { wch: 40 }];
    const compositionSheet = XLSX.utils.json_to_sheet(compositionRows);
    compositionSheet['!cols'] = [{ wch: 10 }, { wch: 28 }, { wch: 16 }, { wch: 28 }, { wch: 16 }, { wch: 12 }, { wch: 28 }, { wch: 16 }, { wch: 28 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 28 }];
    const lookupRows = [
      ...products.map(product => ({
        'Тип': 'Товар', 'ID': product.id, 'Назва': product.name, 'Код B52': product.b52Code ?? '',
        'Назва B52': product.b52Name ?? '', 'Одиниця': product.weightType ?? '',
      })),
      ...items.map(semi => ({
        'Тип': 'Н/Ф', 'ID': semi.id, 'Назва': semi.name, 'Код B52': semi.b52Code ?? '',
        'Назва B52': semi.b52Name ?? '', 'Одиниця': semi.weightType ?? '',
      })),
      ...categories.map(category => ({
        'Тип': 'Категорія Н/Ф', 'ID': category.id, 'Назва': category.name, 'Код B52': '', 'Назва B52': '', 'Одиниця': '',
      })),
      ...preparationOperations.map(operation => ({
        'Тип': 'Операція', 'ID': operation.id, 'Назва': operation.name, 'Код B52': '', 'Назва B52': '', 'Одиниця': '',
      })),
      ...processingMethods.map(method => ({
        'Тип': 'Метод обробки', 'ID': method.id, 'Назва': method.name, 'Код B52': '', 'Назва B52': '',
        'Одиниця': method.defaultTemperatureC == null ? '' : `${method.defaultTemperatureC} °C`,
      })),
    ];
    const promptSheet = XLSX.utils.aoa_to_sheet([
      ['CRM4Chef semi-finished recipe workbook draft v1'],
      ['Заповнюйте лише аркуші «Напівфабрикати» та «Склад»; збережіть назви колонок і аркушів.'],
      ['Використовуйте ID із «Довідник» для товарів, Н/Ф, операцій і методів обробки; не підбирайте неоднозначні позиції лише за назвою.'],
      ['Кроки приготування JSON: масив об’єктів із title, instructions та optional estimatedMinutes (ціле число хвилин).'],
      ['Не створюйте ручні алергени/нутрієнти, фото-URL, HACCP температури, строки зберігання чи інші непогоджені правила.'],
      ['Після генерації файл пройде перевірку CRM; помилки потрібно виправити до збереження. Імпорт кількох API-запитів наразі не є атомарним.'],
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Напівфабрикати');
    XLSX.utils.book_append_sheet(wb, compositionSheet, 'Склад');
    XLSX.utils.book_append_sheet(wb, promptSheet, 'Інструкція AI');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(lookupRows), 'Довідник');
    XLSX.writeFile(wb, `semifinished_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // ── Excel Import ────────────────────────────────────────────
  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportMsg(null);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        setImporting(true);
        const data = new Uint8Array(ev.target!.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets['Напівфабрикати'] ?? wb.Sheets[wb.SheetNames[0]];
        const compositionSheet = wb.Sheets['Склад'];
        const rowsIn: any[] = XLSX.utils.sheet_to_json(ws, { defval: '' });
        if (!rowsIn.length) { setImportMsg('Файл порожній.'); return; }
        const compositionRows: any[] = compositionSheet ? XLSX.utils.sheet_to_json(compositionSheet, { defval: '' }) : [];
        const hasComposition = compositionRows.length > 0;
        const importErrors: string[] = [];
        const numericFields = ['Брутто', 'Нетто', 'Вихід', 'Кількість'];
        const semiFinishedNames = new Set<string>();
        rowsIn.forEach((row, index) => {
          const rowNumber = index + 2;
          const semiName = String(row['Назва'] ?? '').trim();
          const semiKey = normalizeLookup(semiName);
          if (!semiName) importErrors.push(`Напівфабрикати, рядок ${rowNumber}: не вказано назву.`);
          else if (semiFinishedNames.has(semiKey)) importErrors.push(`Напівфабрикати, рядок ${rowNumber}: назва «${semiName}» повторюється у файлі.`);
          semiFinishedNames.add(semiKey);
          for (const field of ['Брутто', 'Нетто']) {
            const value = row[field];
            const parsed = parseRecipeImportNumber(value);
            if (String(value ?? '').trim() && parsed == null)
              importErrors.push(`Напівфабрикати, рядок ${rowNumber}: «${field}» має містити коректне число.`);
            else if (parsed != null && parsed < 0)
              importErrors.push(`Напівфабрикати, рядок ${rowNumber}: «${field}» не може бути від’ємним.`);
          }
          const categoryName = String(row['Категорія'] ?? '').trim();
          if (categoryName && !categories.some(category => normalizeLookup(category.name) === normalizeLookup(categoryName)))
            importErrors.push(`Напівфабрикати, рядок ${rowNumber}: категорію «${categoryName}» не знайдено.`);
          const stepsCell = row['Кроки приготування JSON'];
          if (String(stepsCell ?? '').trim() && parseRecipePreparationSteps(String(stepsCell)) == null)
            importErrors.push(`Напівфабрикати, рядок ${rowNumber}: «Кроки приготування JSON» має некоректний формат.`);
        });
        compositionRows.forEach((row, index) => {
          const rowNumber = index + 2;
          const semiName = String(row['Напівфабрикат'] ?? row['Н/Ф'] ?? '').trim();
          const ingredientName = String(row['Назва інгредієнта'] ?? '').trim();
          const existingParent = resolveSemiFinishedByRow(items, row);
          const matchingNewParents = rowsIn.filter(master => normalizeLookup(master['Назва']) === normalizeLookup(semiName));
          if (!existingParent && matchingNewParents.length !== 1)
            importErrors.push(`Склад, рядок ${rowNumber}: напівфабрикат «${semiName || 'не вказано'}» не знайдено або його відповідність неоднозначна.`);
          if (!ingredientName) importErrors.push(`Склад, рядок ${rowNumber}: не вказано інгредієнт.`);

          const kind = normalizeLookup(row['Тип інгредієнта']);
          if (kind && kind !== 'товар' && kind !== 'н/ф')
            importErrors.push(`Склад, рядок ${rowNumber}: невідомий тип інгредієнта «${kind}». Вкажіть «Товар» або «Н/Ф».`);
          const product = kind === 'н/ф' ? undefined : resolveProductIngredient(row);
          const semi = kind === 'товар' ? undefined : resolveSemiIngredient(items, row);
          if (ingredientName && !product && !semi)
            importErrors.push(`Склад, рядок ${rowNumber}: інгредієнт «${ingredientName}» не знайдений або має неоднозначну відповідність.`);
          else if (ingredientName && !kind && product && semi)
            importErrors.push(`Склад, рядок ${rowNumber}: «${ingredientName}» є і товаром, і Н/Ф — вкажіть тип інгредієнта.`);
          if ((String(row['Операція ID'] ?? '').trim() || String(row['Операція'] ?? '').trim()) && !resolvePreparationOperation(row))
            importErrors.push(`Склад, рядок ${rowNumber}: підготовчу операцію не знайдено або відповідність неоднозначна.`);
          if ((String(row['Метод обробки ID'] ?? '').trim() || String(row['Метод обробки'] ?? '').trim()) && !resolveProcessingMethod(row))
            importErrors.push(`Склад, рядок ${rowNumber}: метод обробки не знайдено або відповідність неоднозначна.`);
          const temperatureCell = row['Температура обробки °C'];
          if (String(temperatureCell ?? '').trim() && parseRecipeImportNumber(temperatureCell) == null)
            importErrors.push(`Склад, рядок ${rowNumber}: температура обробки має бути числом.`);

          for (const field of numericFields) {
            const value = row[field];
            const parsed = parseRecipeImportNumber(value);
            if (String(value ?? '').trim() && parsed == null)
              importErrors.push(`Склад, рядок ${rowNumber}: «${field}» має містити коректне число.`);
            else if (parsed != null && parsed < 0)
              importErrors.push(`Склад, рядок ${rowNumber}: «${field}» не може бути від’ємним.`);
          }
          if ((parseRecipeImportNumber(row['Кількість']) ?? 0) <= 0 && (parseRecipeImportNumber(row['Брутто']) ?? 0) <= 0)
            importErrors.push(`Склад, рядок ${rowNumber}: вкажіть додатну «Кількість» або «Брутто».`);
        });
        if (importErrors.length) {
          setImportMsg(`Імпорт скасовано: ${importErrors.slice(0, 8).join(' ')}${importErrors.length > 8 ? ` Ще помилок: ${importErrors.length - 8}.` : ''}`);
          return;
        }
        let created = 0, updated = 0, compositionsUpdated = 0, skipped = 0;
        const semiPool = [...items];
        for (const row of rowsIn) {
          const name = String(row['Назва'] ?? '').trim();
          if (!name) { skipped++; continue; }
          const existing = resolveSemiFinishedByRow(semiPool, row);
          const catName = String(row['Категорія'] ?? '').trim();
          const catId = catName
            ? (categories.find(c => normalizeLookup(c.name) === normalizeLookup(catName))?.id ?? null)
            : (existing?.categoryId ?? null);
          const wt = String(row['Одиниця виміру'] ?? '');
          const weightType = WEIGHT_TYPES.find(w => w.label === wt || w.value === wt)?.value ?? 'kg';
          const payload = {
            name,
            b52Code: String(row['Код B52'] ?? row['b52Code'] ?? existing?.b52Code ?? '').trim(),
            b52Name: String(row['Назва B52'] ?? row['b52Name'] ?? existing?.b52Name ?? '').trim(),
            categoryId: catId,
            cookingTime: parseDurationToSeconds(String(row['Час приготування (сек або хв,сек)'] ?? existing?.cookingTime ?? '0')),
            serveTime: parseDurationToSeconds(String(row['Час на видачу (сек або хв,сек)'] ?? existing?.serveTime ?? '0')),
            weightType,
            description: String(row['Опис'] ?? existing?.description ?? '').trim(),
            preparationSteps: String(row['Кроки приготування JSON'] ?? '').trim()
              ? parseRecipePreparationSteps(String(row['Кроки приготування JSON'])) ?? []
              : (existing?.preparationSteps ?? []),
            netWeight: parseRecipeImportNumber(row['Нетто'] ?? existing?.netWeight) ?? 0,
            grossWeight: parseRecipeImportNumber(row['Брутто'] ?? existing?.grossWeight) ?? 0,
            excludeFromDishWeightCalc: parseFlag(row['Не рахувати в стравах'], existing?.excludeFromDishWeightCalc ?? false),
            isAuxiliaryRecipe: parseFlag(row['Набір на порцію'], existing?.isAuxiliaryRecipe ?? false),
            items: hasComposition ? [] : (existing?.items ?? []),
          };

          try {
            const result = existing
              ? await dispatch(updateSemiFinished({ id: existing.id, data: payload })).unwrap()
              : await dispatch(createSemiFinished(payload)).unwrap();
            const existingIndex = semiPool.findIndex((item) => item.id === result.id);
            if (existingIndex >= 0) semiPool[existingIndex] = result;
            else semiPool.push(result);
            if (existing) updated++;
            else created++;
          } catch {
            skipped++;
          }
        }

        if (hasComposition) {
          const parentIds = Array.from(new Set(compositionRows
            .map((row) => resolveSemiFinishedByRow(semiPool, row)?.id ?? null)
            .filter((id): id is number => id != null)));

          for (const parentId of parentIds) {
            const parent = semiPool.find((item) => item.id === parentId);
            if (!parent) continue;
            const parentRows = compositionRows.filter((row) => resolveSemiFinishedByRow(semiPool, row)?.id === parentId);
            const resolvedItems = parentRows.map((row) => {
              const type = normalizeLookup(row['Тип інгредієнта']);
              const resolvedProduct = type === 'товар' || !type ? resolveProductIngredient(row) : null;
              const resolvedSemi = type === 'н/ф' || (!type && !resolvedProduct) ? resolveSemiIngredient(semiPool, row) : null;
              if (!resolvedProduct && !resolvedSemi)
                throw new Error(`Інгредієнт «${String(row['Назва інгредієнта'] ?? '').trim()}» не знайдено або відповідність неоднозначна.`);
              return {
                productId: resolvedProduct?.id ?? null,
                subSemiFinishedProductId: resolvedSemi?.id ?? null,
                quantity: parseRecipeImportNumber(row['Кількість'] ?? row['Брутто']) ?? 0,
                bruttoWeight: parseRecipeImportNumber(row['Брутто'] ?? row['Кількість']) ?? 0,
                nettoWeight: parseRecipeImportNumber(row['Нетто']) ?? 0,
                output: parseRecipeImportNumber(row['Вихід']) ?? 0,
                notes: String(row['Нотатка'] ?? '').trim(),
                operationNotes: String(row['Операційна нотатка'] ?? '').trim(),
                preparationOperationId: resolvePreparationOperation(row)?.id ?? null,
                processingMethodId: resolveProcessingMethod(row)?.id ?? null,
                processingTemperatureC: parseRecipeImportNumber(row['Температура обробки °C']),
              };
            });

            try {
              const updatedParent = await dispatch(updateSemiFinished({
                id: parent.id,
                data: buildSemiFinishedPayload(parent, resolvedItems),
              })).unwrap();
              const parentIndex = semiPool.findIndex((item) => item.id === updatedParent.id);
              if (parentIndex >= 0) semiPool[parentIndex] = updatedParent;
              compositionsUpdated++;
            } catch {
              skipped++;
            }
          }
        }
        await dispatch(fetchSemiFinished());
        setImportMsg(`Імпортовано. Створено: ${created}. Оновлено: ${updated}. Складів оновлено: ${compositionsUpdated}. Пропущено: ${skipped}.`);
      } catch {
        setImportMsg('Помилка читання файлу.');
      } finally {
        setImporting(false);
        if (fileRef.current) fileRef.current.value = '';
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const panelStyle: React.CSSProperties = {
    background: 'var(--crm-surface)',
    color: 'var(--crm-text)',
    border: '1px solid var(--crm-border)',
    borderRadius: 'var(--crm-radius-md)',
    boxShadow: 'var(--crm-shadow-sm)',
  };
  const buttonStyle = (tone: 'primary' | 'success' | 'danger' | 'warning' | 'muted' | 'accent'): React.CSSProperties => ({
    background: `var(--crm-${tone === 'muted' ? 'surface-alt' : tone === 'primary' ? 'primary' : tone === 'accent' ? 'accent' : `${tone}-bg`})`,
    color: `var(--crm-${tone === 'muted' ? 'text' : tone === 'primary' ? 'primary-contrast' : tone === 'accent' ? 'accent-contrast' : tone})`,
    border: `1px solid var(--crm-${tone === 'muted' ? 'border' : tone === 'primary' ? 'primary' : tone === 'accent' ? 'accent' : tone})`,
    borderRadius: 'var(--crm-radius-sm)',
    padding: '8px 12px',
  });
  const inp: React.CSSProperties = { border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: '7px 10px', width: '100%', boxSizing: 'border-box', fontSize: '14px', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' };
  const lbl: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '4px', color: 'var(--crm-text)', fontSize: '13px' };
  const th: React.CSSProperties = { padding: '9px 10px', background: 'var(--crm-primary)', color: 'var(--crm-primary-contrast)', textAlign: 'left', fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { padding: '6px 8px', fontSize: '13px', color: 'var(--crm-text)', borderBottom: '1px solid var(--crm-table-row-border)' };
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredItems = items.filter((sf) => !normalizedSearch || sf.name.toLowerCase().includes(normalizedSearch));
  const sortedItems = [...filteredItems].sort((a, b) => {
    switch (sortBy) {
      case 'name-desc': return b.name.localeCompare(a.name, 'uk');
      case 'cost-asc': return a.costPrice - b.costPrice;
      case 'cost-desc': return b.costPrice - a.costPrice;
      case 'category-asc': return (a.categoryName ?? '').localeCompare(b.categoryName ?? '', 'uk');
      case 'category-desc': return (b.categoryName ?? '').localeCompare(a.categoryName ?? '', 'uk');
      case 'name-asc':
      default: return a.name.localeCompare(b.name, 'uk');
    }
  });
  const getSortArrow = (ascKey: SemiFinishedSort, descKey: SemiFinishedSort) => {
    if (sortBy === ascKey) return ' ↑';
    if (sortBy === descKey) return ' ↓';
    return '';
  };
  const toggleSort = (ascKey: SemiFinishedSort, descKey: SemiFinishedSort) => {
    setSortBy((prev) => (prev === ascKey ? descKey : ascKey));
  };
  const totalPages = Math.max(1, Math.ceil(sortedItems.length / PAGE_SIZE));
  const page = Math.min(currentPage, totalPages);
  const pagedItems = sortedItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pageStart = sortedItems.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(page * PAGE_SIZE, sortedItems.length);
  const detailsPortionsValue = Math.max(0, parseFloat(detailsPortions.replace(',', '.')) || 0);
  const detailsRequiredCooking = detailsModal ? calcRequiredTime(detailsModal.semi.cookingTime, detailsPortionsValue) : 0;
  const detailsRequiredServe = detailsModal ? calcRequiredTime(detailsModal.semi.serveTime, detailsPortionsValue) : 0;
  const detailsWorkStartSeconds = parseClockToSeconds(detailsWorkStart);
  const detailsWorkEndSeconds = parseClockToSeconds(detailsWorkEnd);
  const detailsWorkPortionsValue = Math.max(0, parseFloat(detailsWorkPortions.replace(',', '.')) || 0);
  const detailsWorkDurationSeconds = detailsWorkStartSeconds != null && detailsWorkEndSeconds != null
    ? (detailsWorkEndSeconds >= detailsWorkStartSeconds
      ? detailsWorkEndSeconds - detailsWorkStartSeconds
      : (24 * 3600 - detailsWorkStartSeconds) + detailsWorkEndSeconds)
    : 0;
  const detailsWorkSecondsPerPortion = detailsWorkPortionsValue > 0 ? detailsWorkDurationSeconds / detailsWorkPortionsValue : 0;
  const allVisibleSelected = pagedItems.length > 0 && pagedItems.every((sf) => selectedIds.includes(sf.id));
  const replaceSemiSearchItems = items
    .filter((item) => item.id !== replaceSource?.id)
    .map((item) => ({ id: String(item.id), name: item.name }));
  const deleteReplaceSourceSemi = deleteBlockModal?.semiFinishedId != null
    ? items.find((item) => item.id === deleteBlockModal.semiFinishedId) ?? null
    : null;
  const deleteReplaceSemiSearchItems = items
    .filter((item) => item.id !== deleteReplaceSourceSemi?.id)
    .map((item) => ({ id: String(item.id), name: item.name }));
  const selectedCategoryCount = printCategoryIds.length;
  const resolvedPrintPreviewIds = resolvePrintIds(selectedIds, printCategoryIds);

  const togglePrintCategory = (categoryId: number) => {
    setPrintCategoryIds((prev) => prev.includes(categoryId)
      ? prev.filter((id) => id !== categoryId)
      : [...prev, categoryId]);
  };

  const saveCurrentPrintSelection = () => {
    const name = printSelectionName.trim();
    if (!name) {
      setError('Вкажіть назву набору друку.');
      return;
    }
    const next: SavedPrintSelection = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name,
      selectedIds: [...selectedIds],
      categoryIds: [...printCategoryIds],
    };
    setSavedPrintSelections((prev) => [next, ...prev]);
    setPrintSelectionName('');
  };

  const applySavedSelection = (selection: SavedPrintSelection) => {
    setSelectedIds(resolvePrintIds(selection.selectedIds, []));
    setPrintCategoryIds(selection.categoryIds);
  };

  const removeSavedSelection = (selectionId: string) => {
    setSavedPrintSelections((prev) => prev.filter((selection) => selection.id !== selectionId));
  };

  return (
    <div className="page-shell page-shell--wide management-recipe-page semi-finished-management-page" style={{ maxWidth: '1400px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid var(--crm-accent)', paddingBottom: '8px' }}>
        <h2 style={{ color: 'var(--crm-text)', margin: 0 }}>Управління напівфабрикатами</h2>
        <button onClick={openNew} style={{ ...buttonStyle('success'), padding: '9px 18px', cursor: 'pointer', fontWeight: 600 }}>+ Додати</button>
      </div>

      {/* Import / Export toolbar */}
      <div style={{ display: 'flex', gap: '10px', marginTop: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
        <button onClick={handleExport} style={{ ...buttonStyle('success'), cursor: 'pointer', fontWeight: 500 }}>
          ⬇ Експорт Excel
        </button>
        <label style={{ ...buttonStyle('primary'), cursor: importing ? 'not-allowed' : 'pointer', fontWeight: 500, opacity: importing ? 0.7 : 1 }}>
          ⬆ {importing ? 'Імпорт...' : 'Імпорт Excel'}
          <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleImportFile} disabled={importing} style={{ display: 'none' }} />
        </label>
        <button
          type="button"
          onClick={handleRecalculateCosts}
          disabled={recalculatingCosts}
          style={{ ...buttonStyle(recalculatingCosts ? 'muted' : 'success'), cursor: recalculatingCosts ? 'not-allowed' : 'pointer', fontWeight: 500 }}
        >
          {recalculatingCosts ? 'Перерахунок...' : 'Перерахувати собівартість Н/Ф + Страв'}
        </button>
        <button
          type="button"
          onClick={() => setB52ModalOpen(true)}
          style={{ ...buttonStyle('accent'), cursor: 'pointer', fontWeight: 500 }}
        >
          B52 Довідник
        </button>
        <span style={{ fontSize: '12px', color: 'var(--crm-text-muted)' }}>Колонки імпорту: <strong>Назва</strong>, Категорія, Час приготування, Час на видачу, Одиниця виміру, Брутто, Нетто, Опис</span>
      </div>
      {importMsg && (
        <div style={{ ...panelStyle, marginTop: '8px', padding: '10px', fontSize: '14px', background: importMsg.startsWith('Імпортовано') ? 'var(--crm-success-bg)' : 'var(--crm-danger-bg)', color: importMsg.startsWith('Імпортовано') ? 'var(--crm-success)' : 'var(--crm-danger)' }}>
          {importMsg}
        </div>
      )}
      {error && (
        <div style={{ ...panelStyle, marginTop: '8px', padding: '10px', fontSize: '14px', background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)' }}>
          {error}
        </div>
      )}

      <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Пошук напівфабрикату..."
          style={{ ...inp, maxWidth: '320px' }}
        />
        <button
          type="button"
          onClick={handleBulkDelete}
          disabled={selectedIds.length === 0}
          style={{ ...buttonStyle(selectedIds.length === 0 ? 'muted' : 'danger'), cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer', fontWeight: 500 }}
        >
          Видалити вибрані ({selectedIds.length})
        </button>
        <button
          type="button"
          onClick={handlePrintCheatSheets}
          disabled={selectedIds.length === 0}
          title="Друкувати шпаргалки для обраних Н/Ф"
          style={{ ...buttonStyle(selectedIds.length === 0 ? 'muted' : 'accent'), cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer', fontWeight: 500 }}
        >
          🖨 Шпаргалки ({selectedIds.length})
        </button>
        <button
          type="button"
          onClick={() => setPrintManageModalOpen(true)}
          style={{ ...buttonStyle('muted'), cursor: 'pointer', fontWeight: 500 }}
        >
          Керування друком
        </button>
      </div>

      {/* List */}
      {loading ? <p>Завантаження...</p> : (
        <table className="management-recipe-list semi-finished-list-table" style={{ ...panelStyle, width: '100%', borderCollapse: 'collapse', overflow: 'hidden', marginTop: '20px' }}>
          <thead>
            <tr>
              <th style={th}>
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={(e) => {
                    const visibleIds = pagedItems.map((sf) => sf.id);
                    setSelectedIds((prev) => e.target.checked
                      ? Array.from(new Set([...prev, ...visibleIds]))
                      : prev.filter((id) => !visibleIds.includes(id)));
                  }}
                />
              </th>
              <th style={th}>#</th>
              <th
                style={{ ...th, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => toggleSort('name-asc', 'name-desc')}
                title="Сортувати за назвою"
              >
                Назва{getSortArrow('name-asc', 'name-desc')}
              </th>
              <th
                style={{ ...th, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => toggleSort('category-asc', 'category-desc')}
                title="Сортувати за категорією"
              >
                Категорія{getSortArrow('category-asc', 'category-desc')}
              </th>
              <th style={th}>Алергени</th>
              <th style={th}>Тип ваги</th>
              <th
                style={{ ...th, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => toggleSort('cost-asc', 'cost-desc')}
                title="Сортувати за собівартістю"
              >
                Собівартість{getSortArrow('cost-asc', 'cost-desc')}
              </th>
              <th style={th}>Дії</th>
            </tr>
          </thead>
          <tbody>
            {pagedItems.map((sf, i) => (
                <tr key={sf.id} style={{ background: i % 2 === 0 ? 'var(--crm-surface-alt)' : 'var(--crm-surface)' }}>
                  <td data-label="Обрати" style={td}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(sf.id)}
                      onChange={(e) => setSelectedIds((prev) => e.target.checked ? [...prev, sf.id] : prev.filter((id) => id !== sf.id))}
                    />
                  </td>
                  <td data-label="#" style={td}>{pageStart + i}</td>
                  <td data-label="Назва" style={{ ...td, fontWeight: 500 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                      <button
                        type="button"
                        onClick={() => openDetailsModal(sf)}
                        style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', color: 'var(--crm-text)', cursor: 'pointer', fontWeight: 600 }}
                      >
                        {sf.name}
                      </button>
                      {(sf.b52Code || sf.b52Name) && (
                        <span style={{ fontSize: '11px', color: 'var(--crm-text-muted)' }}>
                          {[sf.b52Code ? `Код: ${sf.b52Code}` : '', sf.b52Name ? `B52: ${sf.b52Name}` : ''].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td data-label="Категорія" style={td}>{sf.categoryName ?? '—'}</td>
                  <td data-label="Алергени" style={{ ...td, minWidth: '100px' }}>
                    {sf.allergens.length > 0 ? (
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {sf.allergens.map((a) => (
                          <span key={a.id} title={a.name} style={{ fontSize: '13px', lineHeight: 1 }}>
                            {getAllergenIcon(a.name)}
                          </span>
                        ))}
                      </div>
                    ) : '—'}
                  </td>
                  <td data-label="Тип ваги" style={td}>{WEIGHT_TYPES.find(w => w.value === sf.weightType)?.label ?? sf.weightType}</td>
                  <td data-label="Собівартість" style={{ ...td, fontWeight: 600, color: 'var(--crm-success)' }}>{sf.costPrice.toFixed(2)} ₴</td>
                  <td data-label="Керування" style={td}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={() => openEdit(sf)} style={{ ...buttonStyle('primary'), padding: '5px 10px', cursor: 'pointer' }}>Ред.</button>
                      <button onClick={() => openUsageModal(sf)} style={{ ...buttonStyle('accent'), padding: '5px 10px', cursor: 'pointer' }}>Входження</button>
                      <button onClick={() => openReplaceModal(sf)} style={{ ...buttonStyle('warning'), padding: '5px 10px', cursor: 'pointer' }}>Заміна</button>
                      <button onClick={() => handlePrintSemi(sf)} title="Друкувати калькуляцію" style={{ ...buttonStyle('accent'), padding: '5px 10px', cursor: 'pointer' }}>🖨</button>
                      <button onClick={() => handlePrintSemi(sf, true)} title="Друк для бухгалтера" style={{ ...buttonStyle('success'), padding: '5px 10px', cursor: 'pointer' }}>Бух.</button>
                      <button onClick={() => handleDelete(sf.id)} style={{ ...buttonStyle('danger'), padding: '5px 10px', cursor: 'pointer' }}>Вид.</button>
                    </div>
                  </td>
                </tr>
            ))}
            {sortedItems.length === 0 && <tr><td colSpan={8} style={{ ...td, padding: '20px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Напівфабрикати відсутні</td></tr>}
          </tbody>
        </table>
      )}
      {!loading && sortedItems.length > 0 && (
        <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--crm-text-muted)', fontSize: '13px' }}>
            Показано {pageStart}-{pageEnd} з {sortedItems.length}
          </span>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              style={{ ...buttonStyle(page <= 1 ? 'muted' : 'primary'), padding: '6px 12px', cursor: page <= 1 ? 'not-allowed' : 'pointer' }}
            >
              ← Назад
            </button>
            <span style={{ minWidth: '100px', textAlign: 'center', color: 'var(--crm-text)', fontSize: '13px' }}>
              Сторінка {page} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              style={{ ...buttonStyle(page >= totalPages ? 'muted' : 'primary'), padding: '6px 12px', cursor: page >= totalPages ? 'not-allowed' : 'pointer' }}
            >
              Вперед →
            </button>
          </div>
        </div>
      )}

      {detailsModal && (
        <div className="semi-finished-details-overlay semi-details-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 2090, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div className="semi-finished-details-modal semi-details-modal" style={{ ...panelStyle, width: '100%', maxWidth: '760px', maxHeight: '80vh', overflow: 'auto', padding: '18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div className="semi-finished-details-header semi-details-modal__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: 'var(--crm-text)' }}>{detailsModal.semi.name}</h3>
              <div className="semi-finished-details-actions semi-details-modal__actions" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <SendPdfToTelegramButton endpoint={`/semifinishedproducts/${detailsModal.semi.id}/send-telegram`} />
                <button
                  type="button"
                  onClick={() => {
                    if (!productionFormOpen) {
                      const pending = manualProduction.begin(detailsModal.semi.id);
                      if (pending) {
                        setProductionQuantity(String(pending.quantity));
                        setProductionDate(pending.date);
                        setProductionNotes(pending.notes ?? '');
                      }
                    }
                    setProductionFormOpen((open) => !open);
                    setProductionError(null);
                  }}
                  disabled={productionSaving || manualProduction.blocked}
                  style={{ ...buttonStyle('success'), padding: '7px 12px', cursor: 'pointer', fontWeight: 600 }}
                >
                  + Створити виробництво
                </button>
                <button
                  type="button"
                  onClick={() => printSemiDetailsModal(detailsModal)}
                  style={{ ...buttonStyle('muted'), padding: '7px 12px', cursor: 'pointer', fontWeight: 600 }}
                >
                  🖨 Друк
                </button>
                <button type="button" onClick={() => { detailsRequest.current += 1; setDetailsModal(null); }} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', lineHeight: 1, color: 'var(--crm-text)' }}>×</button>
              </div>
            </div>
            {manualProduction.blocked && <div role="alert" style={{ color: 'var(--crm-danger)' }}>
              Створення виробництва недоступне в цьому контексті. Оберіть власний робочий ресторан або перевірте доступ.
            </div>}
            {!productionFormOpen && productionError && <div role="alert" style={{ color: 'var(--crm-danger)' }}>
              {productionError}
            </div>}
            <div className="semi-finished-details-meta semi-details-modal__meta" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px', color: 'var(--crm-text)' }}>
              <div>Категорія: <strong>{detailsModal.semi.categoryName ?? '—'}</strong></div>
              <div>Одиниця: <strong>{WEIGHT_TYPES.find((w) => w.value === detailsModal.semi.weightType)?.label ?? detailsModal.semi.weightType}</strong></div>
              <div>Час приготування: <strong>{formatDurationLabel(detailsModal.semi.cookingTime)}</strong></div>
              <div>Час викладки: <strong>{formatDurationLabel(detailsModal.semi.serveTime)}</strong></div>
              <div>Брутто: <strong>{detailsModal.semi.grossWeight.toFixed(3)}</strong></div>
              <div>Нетто/вихід: <strong>{detailsModal.semi.netWeight.toFixed(3)}</strong></div>
              <div style={{ gridColumn: '1 / -1' }}>
                Алергени:{' '}
                <strong>
                  {getSemiComputedAllergens(detailsModal.semi).length > 0
                    ? getSemiComputedAllergens(detailsModal.semi).map((a) => `${getAllergenIcon(a.name)} ${a.name}`).join(', ')
                    : '—'}
                </strong>
              </div>
            </div>
            <RecipeCalculationCard
              title="Калькуляційна карта напівфабрикату"
              rows={detailsModal.semi.items.map((item) => ({
                id: item.id,
                name: item.productName || item.subSemiFinishedProductName || '—',
                grossWeight: item.bruttoWeight,
                netWeight: item.nettoWeight,
                output: item.output,
              }))}
            />
            <div className="semi-finished-details-portion-grid semi-details-modal__portion-grid" style={{ border: '1px solid var(--crm-border)', borderRadius: '8px', padding: '10px 12px', background: 'var(--crm-surface-alt)', display: 'grid', gridTemplateColumns: 'minmax(180px, 220px) 1fr', gap: '12px', alignItems: 'end' }}>
              <label style={{ ...lbl, margin: 0 }}>
                Необхідна кількість порцій
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  style={inp}
                  value={detailsPortions}
                  onChange={(e) => setDetailsPortions(e.target.value)}
                  placeholder="0"
                />
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '10px' }}>
                {[
                  ['Потрібний час приготування', formatDurationLabel(detailsRequiredCooking)],
                  ['Потрібний час на видачу', formatDurationLabel(detailsRequiredServe)],
                  ['Разом', formatDurationLabel(detailsRequiredCooking + detailsRequiredServe)],
                ].map(([label, value]) => (
                  <div key={label} style={{ border: '1px solid var(--crm-border)', borderRadius: '8px', background: 'var(--crm-surface)', padding: '10px 12px', minHeight: '68px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '6px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--crm-text-muted)', fontWeight: 600 }}>{label}</span>
                    <strong style={{ fontSize: '15px', color: 'var(--crm-text)', lineHeight: 1.25, fontVariantNumeric: 'tabular-nums' }}>{value}</strong>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ border: '1px solid var(--crm-border)', borderRadius: '8px', padding: '10px 12px', background: 'var(--crm-surface-alt)', display: 'grid', gap: '10px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--crm-text)' }}>
                Калькулятор часу на 1 порцію (за фактом роботи)
              </div>
              <div className="crm-mobile-form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(140px, 1fr))', gap: '10px' }}>
                <label style={{ ...lbl, margin: 0 }}>
                  Початок роботи
                  <input
                    type="time"
                    style={inp}
                    value={detailsWorkStart}
                    onChange={(e) => setDetailsWorkStart(e.target.value)}
                  />
                </label>
                <label style={{ ...lbl, margin: 0 }}>
                  Кінець роботи
                  <input
                    type="time"
                    style={inp}
                    value={detailsWorkEnd}
                    onChange={(e) => setDetailsWorkEnd(e.target.value)}
                  />
                </label>
                <label style={{ ...lbl, margin: 0 }}>
                  Виготовлено порцій
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    style={inp}
                    value={detailsWorkPortions}
                    onChange={(e) => setDetailsWorkPortions(e.target.value)}
                    placeholder="0"
                  />
                </label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '10px' }}>
                {[
                  ['Тривалість роботи', formatDurationLabel(detailsWorkDurationSeconds)],
                  ['На 1 порцію', detailsWorkDurationSeconds > 0 && detailsWorkPortionsValue > 0 ? formatDurationLabel(detailsWorkSecondsPerPortion) : '—'],
                ].map(([label, value]) => (
                  <div key={label} style={{ border: '1px solid var(--crm-border)', borderRadius: '8px', background: 'var(--crm-surface)', padding: '10px 12px', minHeight: '68px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '6px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--crm-text-muted)', fontWeight: 600 }}>{label}</span>
                    <strong style={{ fontSize: '15px', color: 'var(--crm-text)', lineHeight: 1.25, fontVariantNumeric: 'tabular-nums' }}>{value}</strong>
                  </div>
                ))}
              </div>
            </div>
            {detailsModal.semi.description && (
              <div style={{ fontSize: '13px', color: 'var(--crm-text)', lineHeight: 1.5 }}>
                Опис: <strong>{detailsModal.semi.description}</strong>
              </div>
            )}
            <div style={{ borderTop: '1px solid var(--crm-border)', paddingTop: '10px' }}>
              <div style={{ fontWeight: 600, color: 'var(--crm-text)', marginBottom: '8px' }}>Входження у стравах та Н/Ф</div>
              {detailsModal.usageEntries.length === 0 ? (
                <div style={{ color: 'var(--crm-text-muted)' }}>Немає входжень у стравах або напівфабрикатах.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--crm-surface-alt)' }}>
                      <th style={{ ...td, fontWeight: 700 }}>Тип</th>
                      <th style={{ ...td, fontWeight: 700 }}>ID</th>
                      <th style={{ ...td, fontWeight: 700 }}>Назва</th>
                      <th style={{ ...td, fontWeight: 700 }}>Кількість продукту/н/ф</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailsModal.usageEntries.map((entry) => (
                      <tr key={`details-usage-${entry.kind}-${entry.id}`} style={{ borderTop: '1px solid var(--crm-table-row-border)' }}>
                        <td style={td}>{entry.kind === 'dish' ? 'Страва' : 'Н/Ф'}</td>
                        <td style={td}>{entry.id}</td>
                        <td style={{ ...td, fontWeight: 500 }}>{entry.name}</td>
                        <td style={td}>{entry.quantity.toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div style={{ borderTop: '1px solid var(--crm-border)', paddingTop: '10px' }}>
              {productionFormOpen && (
                <div style={{ border: '1px solid var(--crm-success)', borderRadius: 'var(--crm-radius-md)', padding: '12px', marginBottom: '12px', background: 'var(--crm-surface-alt)', display: 'grid', gap: '10px' }}>
                  <div style={{ fontWeight: 700, color: 'var(--crm-text)' }}>Новий позитивний документ виробництва</div>
                  <div className="semi-finished-production-fields" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <label style={{ ...lbl, margin: 0 }}>
                      Кількість *
                      <input
                        type="number"
                        min="0.0001"
                        step="0.0001"
                        style={inp}
                        value={productionQuantity}
                        onChange={(e) => setProductionQuantity(e.target.value)}
                        placeholder="0"
                        disabled={productionSaving || !!productionOperation || manualProduction.blocked}
                      />
                    </label>
                    <label style={{ ...lbl, margin: 0 }}>
                      Дата виробництва *
                      <input
                        type="date"
                        style={inp}
                        value={productionDate}
                        onChange={(e) => setProductionDate(e.target.value)}
                        disabled={productionSaving || !!productionOperation || manualProduction.blocked}
                      />
                    </label>
                  </div>
                  <label style={{ ...lbl, margin: 0 }}>
                    Примітка
                    <textarea
                      style={{ ...inp, minHeight: '58px', resize: 'vertical' }}
                      value={productionNotes}
                      onChange={(e) => setProductionNotes(e.target.value)}
                      placeholder="Наприклад: підготовлено для заходу 25.09"
                      disabled={productionSaving || !!productionOperation || manualProduction.blocked}
                    />
                  </label>
                  {productionError && <div role="alert" style={{ color: 'var(--crm-danger)', fontSize: '13px' }}>{productionError}</div>}
                  {productionOperation && <div role="status" style={{ color: 'var(--crm-text-muted)', fontSize: '13px' }}>
                    {productionOperation.confirmed
                      ? 'Документ уже оброблено. Повторна дія лише оновить історію без нового створення.'
                      : 'Результат попередньої спроби невідомий. Повтор буде надіслано з тими самими даними та ключем.'}
                  </div>}
                  <div className="semi-finished-production-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => setProductionFormOpen(false)}
                      disabled={productionSaving}
                      style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: '6px', padding: '7px 12px', cursor: productionSaving ? 'not-allowed' : 'pointer' }}
                    >
                      Скасувати
                    </button>
                    <button
                      type="button"
                      onClick={createProduction}
                      disabled={productionSaving || manualProduction.blocked}
                      style={{ ...buttonStyle(productionSaving ? 'muted' : 'success'), padding: '7px 12px', cursor: productionSaving ? 'not-allowed' : 'pointer', fontWeight: 600 }}
                    >
                      {productionSaving ? 'Збереження...' : productionOperation?.confirmed
                        ? 'Оновити історію виробництва'
                        : productionOperation ? 'Повторити з початковими даними' : 'Зберегти виробництво'}
                    </button>
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => setDetailsModal((prev) => prev ? { ...prev, activeReportTab: 'usage-report' } : prev)}
                  style={{
                    border: '1px solid var(--crm-border)', borderRadius: '999px', padding: '6px 12px',
                    cursor: 'pointer', fontWeight: 600,
                    background: detailsModal.activeReportTab === 'usage-report' ? 'var(--crm-primary)' : 'var(--crm-surface-alt)',
                    color: detailsModal.activeReportTab === 'usage-report' ? 'var(--crm-primary-contrast)' : 'var(--crm-text)',
                  }}
                >
                  Звіт по реалізації
                </button>
                <button
                  type="button"
                  onClick={() => setDetailsModal((prev) => prev ? { ...prev, activeReportTab: 'production' } : prev)}
                  style={{
                    border: '1px solid var(--crm-border)', borderRadius: '999px', padding: '6px 12px',
                    cursor: 'pointer', fontWeight: 600,
                    background: detailsModal.activeReportTab === 'production' ? 'var(--crm-primary)' : 'var(--crm-surface-alt)',
                    color: detailsModal.activeReportTab === 'production' ? 'var(--crm-primary-contrast)' : 'var(--crm-text)',
                  }}
                >
                  Виробництво
                </button>
              </div>
              {detailsModal.reportLoading ? (
                <div style={{ color: 'var(--crm-text-muted)' }}>Завантаження звіту...</div>
              ) : detailsModal.reportError ? (
                <div style={{ color: 'var(--crm-danger)' }}>{detailsModal.reportError}</div>
              ) : detailsModal.activeReportTab === 'usage-report' ? (
                detailsModal.usageReportEntries.length === 0 ? (
                  <div style={{ color: 'var(--crm-text-muted)' }}>За останні 60 днів списань Н/Ф не знайдено.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--crm-surface-alt)' }}>
                        <th style={{ ...td, fontWeight: 700 }}>Дата</th>
                        <th style={{ ...td, fontWeight: 700 }}>Документ</th>
                        <th style={{ ...td, fontWeight: 700 }}>Тип</th>
                        <th style={{ ...td, fontWeight: 700 }}>Кількість</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailsModal.usageReportEntries.map((entry) => (
                        <tr key={`${entry.kind}-${entry.documentId}-${entry.date}`} style={{ borderTop: '1px solid var(--crm-table-row-border)' }}>
                          <td style={td}>{new Date(entry.date).toLocaleDateString('uk-UA')}</td>
                          <td style={{ ...td, fontWeight: 500 }}>{entry.documentName}</td>
                          <td style={td}>{entry.kind === 'order' ? 'Реалізація / захід' : 'Списання'}</td>
                          <td style={td}>{entry.quantity.toFixed(4)} {detailsModal.semi.weightType}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              ) : detailsModal.productionEntries.length === 0 ? (
                <div style={{ color: 'var(--crm-text-muted)' }}>За останні 60 днів виробництва Н/Ф не знайдено.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--crm-surface-alt)' }}>
                      <th style={{ ...td, fontWeight: 700 }}>Дата виробництва</th>
                      <th style={{ ...td, fontWeight: 700 }}>Кількість</th>
                      <th style={{ ...td, fontWeight: 700 }}>Підготував</th>
                      <th style={{ ...td, fontWeight: 700 }}>Підтверджено</th>
                      <th style={{ ...td, fontWeight: 700 }}>Примітка</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailsModal.productionEntries.map((entry) => (
                      <tr key={entry.id} style={{ borderTop: '1px solid var(--crm-table-row-border)' }}>
                        <td style={td}>{new Date(entry.date).toLocaleDateString('uk-UA')}</td>
                        <td style={td}>{entry.quantity.toFixed(4)} {detailsModal.semi.weightType}</td>
                        <td style={{ ...td, fontWeight: 500 }}>{entry.preparedByUsername || '—'}</td>
                        <td style={td}>{new Date(entry.preparedAtUtc).toLocaleString('uk-UA')}</td>
                        <td style={td}>{entry.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="recipe-editor-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '24px 16px' }}>
          <div className="recipe-editor-modal recipe-editor-modal--semi-finished" style={{ ...panelStyle, borderRadius: 'var(--crm-radius-lg)', width: '100%', maxWidth: '1100px', boxShadow: 'var(--crm-shadow-sm)', marginBottom: '24px' }}>

            {/* Header */}
            <div className="recipe-editor-header" style={{ background: 'var(--crm-primary)', color: 'var(--crm-primary-contrast)', padding: '16px 20px', borderRadius: 'var(--crm-radius-lg) var(--crm-radius-lg) 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '16px' }}>{editingId ? `Редагування: ${form.name}` : 'Новий напівфабрикат'}</h3>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', color: 'var(--crm-primary-contrast)', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            <div className="recipe-editor-content" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Row 1: Name + Category + WeightType */}
              <div className="recipe-editor-basic-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 2fr) repeat(5, minmax(120px, 1fr))', gap: '12px' }}>
                <label style={lbl}>
                  Назва *
                  <input style={inp} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Назва напівфабрикату" />
                </label>
                <B52Autocomplete
                  b52Code={form.b52Code}
                  b52Name={form.b52Name}
                  onChange={(code, name) => setForm({ ...form, b52Code: code, b52Name: name })}
                  labelStyle={lbl}
                  inputStyle={inp}
                />
                <label style={lbl}>
                  Категорія
                  <select style={inp} value={form.categoryId} onChange={e => setForm({ ...form, categoryId: e.target.value })}>
                    <option value="">— Не обрано —</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </label>
                <label style={lbl}>
                  Одиниця виміру
                  <select style={inp} value={form.weightType} onChange={e => setForm({ ...form, weightType: e.target.value })}>
                    {WEIGHT_TYPES.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
                  </select>
                </label>
                <label style={lbl}>
                  Час приготування (сек або хв,сек)
                  <input style={inp} value={form.cookingTime} onChange={e => setForm({ ...form, cookingTime: e.target.value })} placeholder="напр. 95 або 1,35" />
                </label>
                <label style={lbl}>
                  Час на видачу (сек або хв,сек)
                  <input style={inp} value={form.serveTime} onChange={e => setForm({ ...form, serveTime: e.target.value })} placeholder="напр. 45 або 0,45" />
                </label>
              </div>

              {/* Description - full width */}
              <label style={lbl}>
                Опис
                <textarea style={{ ...inp, resize: 'vertical', minHeight: '90px' }}
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="Опис технології приготування, особливостей зберігання тощо" />
              </label>
              <RecipePreparationStepsEditor
                value={form.preparationSteps}
                onChange={preparationSteps => setForm(current => ({ ...current, preparationSteps }))}
                onUploadPhoto={uploadPreparationStepPhoto}
                canUploadPhoto={editingId != null}
              />

              {/* Ingredients */}
              <div className="recipe-editor-ingredients" style={{ border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', minWidth: 0 }}>
              <div className="recipe-editor-section-header" style={{ background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong style={{ fontSize: '14px' }}>Склад напівфабрикату</strong>
                  <div className="recipe-editor-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button type="button" onClick={() => setIngredientColumns((previous) => ({ ...previous, loss: !previous.loss }))} style={{ ...buttonStyle(ingredientColumns.loss ? 'warning' : 'muted'), padding: '5px 10px', cursor: 'pointer', fontSize: '12px' }}>
                      {ingredientColumns.loss ? 'Сховати втрати' : 'Показати % втрат'}
                    </button>
                    <button type="button" onClick={() => setIngredientColumns((previous) => ({ ...previous, price: !previous.price }))} style={{ ...buttonStyle(ingredientColumns.price ? 'success' : 'muted'), padding: '5px 10px', cursor: 'pointer', fontSize: '12px' }}>
                      {ingredientColumns.price ? 'Сховати ціну' : 'Показати ціну'}
                    </button>
                    <button type="button" onClick={() => setIngredientColumns((previous) => ({ ...previous, cost: !previous.cost }))} style={{ ...buttonStyle(ingredientColumns.cost ? 'primary' : 'muted'), padding: '5px 10px', cursor: 'pointer', fontSize: '12px' }}>
                      {ingredientColumns.cost ? 'Сховати вартість' : 'Показати вартість'}
                    </button>
                    <button type="button" onClick={() => setIngredientColumns((previous) => ({ ...previous, notes: !previous.notes }))} style={{ ...buttonStyle(ingredientColumns.notes ? 'accent' : 'muted'), padding: '5px 10px', cursor: 'pointer', fontSize: '12px' }}>
                      {ingredientColumns.notes ? 'Сховати нотатки' : 'Показати нотатки'}
                    </button>
                    <button type="button" onClick={() => setIngredientColumns((previous) => ({ ...previous, processing: !previous.processing }))} style={{ ...buttonStyle(ingredientColumns.processing ? 'danger' : 'muted'), padding: '5px 10px', cursor: 'pointer', fontSize: '12px' }}>
                      {ingredientColumns.processing ? 'Сховати обробку та °C' : 'Показати обробку та °C'}
                    </button>
                    <button type="button" onClick={() => setShowQuickProduct(v => !v)} style={{ ...buttonStyle('success'), padding: '5px 12px', cursor: 'pointer', fontSize: '13px' }}>
                      {showQuickProduct ? 'Сховати продукт' : '+ Новий продукт'}
                    </button>
                    <button type="button" onClick={addRow} style={{ ...buttonStyle('primary'), padding: '5px 16px', cursor: 'pointer', fontSize: '13px' }}>+ Рядок</button>
                  </div>
                </div>

                {showQuickProduct && (
                  <div className="recipe-editor-quick-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: '8px', padding: '10px 16px', background: 'var(--crm-success-bg)', borderBottom: '1px solid var(--crm-border)' }}>
                    <input style={{ ...inp, padding: '6px 8px' }} value={quickProduct.name} onChange={(e) => setQuickProduct({ ...quickProduct, name: e.target.value })} placeholder="Назва нового продукту" />
                    <input type="number" min="0" step="0.01" style={{ ...inp, padding: '6px 8px' }} value={quickProduct.price} onChange={(e) => setQuickProduct({ ...quickProduct, price: e.target.value })} placeholder="Ціна" />
                    <select style={{ ...inp, padding: '6px 8px' }} value={quickProduct.weightType} onChange={(e) => setQuickProduct({ ...quickProduct, weightType: e.target.value })}>
                      {WEIGHT_TYPES.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
                    </select>
                    <select style={{ ...inp, padding: '6px 8px' }} value={quickProduct.categoryId} onChange={(e) => setQuickProduct({ ...quickProduct, categoryId: e.target.value })}>
                      <option value="">— Категорія —</option>
                      {productCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <button type="button" disabled={creatingProduct} onClick={handleQuickCreateProduct} style={{ ...buttonStyle(creatingProduct ? 'muted' : 'success'), padding: '0 14px', cursor: creatingProduct ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600 }}>
                      {creatingProduct ? 'Створення...' : 'Додати'}
                    </button>
                  </div>
                )}

                <div className="recipe-editor-ingredients-scroll" style={{ minHeight: '220px', maxWidth: '100%', overflowX: 'auto' }}>
                  <table className="recipe-ingredient-table" style={{ width: '100%', minWidth: ingredientColumns.loss || ingredientColumns.price || ingredientColumns.cost || ingredientColumns.notes || ingredientColumns.processing ? '1320px' : '720px', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr>
                        <th style={{ ...th, padding: '10px 10px' }}>Інгредієнт / брутто</th>
                        <th style={{ ...th, width: '90px', padding: '10px 10px' }}>Нетто</th>
                        <th style={{ ...th, width: '90px', padding: '10px 10px' }}>Вихід</th>
                        {ingredientColumns.loss && <th style={{ ...th, width: '95px', padding: '10px 10px' }}>% втрат</th>}
                        {ingredientColumns.price && <th style={{ ...th, width: '85px', padding: '10px 10px' }}>Ціна/од</th>}
                        {ingredientColumns.cost && <th style={{ ...th, width: '95px', padding: '10px 10px' }}>Вартість</th>}
                        {ingredientColumns.notes && <th style={{ ...th, width: '150px', padding: '10px 10px' }}>Нотатки</th>}
                        {ingredientColumns.processing && <th style={{ ...th, width: '130px', padding: '10px 10px' }}>Обробка</th>}
                        {ingredientColumns.processing && <th style={{ ...th, width: '70px', padding: '10px 10px' }}>°C</th>}
                        <th style={{ ...th, width: '140px', padding: '10px 10px' }}>Операція</th>
                        <th style={{ ...th, width: '170px', padding: '10px 10px' }}>Опис операції</th>
                        <th style={{ ...th, width: '38px', padding: '10px 10px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(row => {
                        const brutto = parseFloat(row.bruttoWeight) || 0;
                        const unitPrice = row.type === 'product'
                          ? (products.find(p => p.id === parseInt(row.refId))?.price ?? 0)
                          : getSFPrice(parseInt(row.refId));
                        const rowCost = unitPrice * brutto;
                        const rowLossPercent = calcRowLossPercent(row);
                        const selOp = preparationOperations.find(o => String(o.id) === row.preparationOperationId);
                        const ingredientWeightType = row.type === 'product'
                          ? products.find(product => product.id === parseInt(row.refId))?.weightType
                          : items.find(item => item.id === parseInt(row.refId))?.weightType;
                        const ingredientUnit = WEIGHT_TYPES.find(weight => weight.value === ingredientWeightType)?.label;

                        return (
                          <tr key={row._key}>
                            <td data-label="Інгредієнт" style={{ ...td, padding: '8px 8px' }}>
                              <ItemQuantityField
                                items={allIngredients}
                                value={encodeIngredientId(row)}
                                onChange={id => {
                                  const decoded = decodeIngredientId(id);
                                  updateRow(row._key, { type: decoded.type, refId: decoded.refId });
                                }}
                                quantity={row.bruttoWeight}
                                onQuantityChange={bruttoWeight => updateRow(row._key, { bruttoWeight })}
                                label="Інгредієнт"
                                quantityLabel="Брутто"
                                unit={ingredientUnit}
                                min={0}
                                step={0.001}
                                quantityRequired={false}
                                placeholder="🔍 Продукт або напівфабрикат..."
                              />
                            </td>
                            <td data-label="Нетто" style={{ ...td, padding: '8px 8px' }}>
                              <input type="number" min="0" step="0.001" style={{ ...inp, padding: '5px', fontSize: '13px' }} value={row.nettoWeight} onChange={e => updateRow(row._key, { nettoWeight: e.target.value })} placeholder="0" />
                            </td>
                            <td data-label="Вихід" style={{ ...td, padding: '8px 8px' }}>
                              <input type="number" min="0" step="0.001" style={{ ...inp, padding: '5px', fontSize: '13px' }} value={row.output} onChange={e => updateRow(row._key, { output: e.target.value })} placeholder="0" />
                            </td>
                            {ingredientColumns.loss && <td data-label="% втрат" style={{ ...td, fontWeight: 600, color: rowLossPercent > 0 ? 'var(--crm-warning)' : 'var(--crm-text-muted)', padding: '8px 8px' }}>{rowLossPercent.toFixed(2)}%</td>}
                            {ingredientColumns.price && <td data-label="Ціна/од" style={{ ...td, color: 'var(--crm-text-muted)', padding: '8px 8px' }}>{unitPrice > 0 ? `${unitPrice.toFixed(2)} ₴` : '—'}</td>}
                            {ingredientColumns.cost && <td data-label="Вартість" style={{ ...td, fontWeight: 600, color: rowCost > 0 ? 'var(--crm-success)' : 'var(--crm-text-muted)', padding: '8px 8px' }}>{rowCost > 0 ? `${rowCost.toFixed(2)} ₴` : '—'}</td>}
                            {ingredientColumns.notes && <td data-label="Нотатки" style={{ ...td, padding: '8px 8px' }}>
                              <input type="text" style={{ ...inp, padding: '5px', fontSize: '13px' }} value={row.notes} onChange={e => updateRow(row._key, { notes: e.target.value })} placeholder="Нотатка..." />
                            </td>}
                            {ingredientColumns.processing && <td data-label="Обробка" style={{ ...td, padding: '8px 8px' }}>
                              <select
                                style={{ ...inp, padding: '5px', fontSize: '12px' }}
                                value={row.processingMethodId}
                                onChange={e => {
                                  const method = processingMethods.find((item) => String(item.id) === e.target.value);
                                  updateRow(row._key, {
                                    processingMethodId: e.target.value,
                                    processingTemperatureC: method?.defaultTemperatureC != null ? String(method.defaultTemperatureC) : row.processingTemperatureC,
                                  });
                                }}
                              >
                                <option value="">— без обробки —</option>
                                {processingMethods.map(method => (
                                  <option key={method.id} value={String(method.id)}>{method.name}</option>
                                ))}
                              </select>
                            </td>}
                            {ingredientColumns.processing && <td data-label="Температура °C" style={{ ...td, padding: '8px 8px' }}>
                              <input type="number" step="1" style={{ ...inp, padding: '5px', fontSize: '13px' }} value={row.processingTemperatureC} onChange={e => updateRow(row._key, { processingTemperatureC: e.target.value })} placeholder="°C" />
                            </td>}
                            <td data-label="Операція" style={{ ...td, padding: '8px 8px' }}>
                              <select
                                style={{ ...inp, padding: '5px', fontSize: '12px', borderLeft: selOp ? `3px solid ${selOp.color}` : undefined }}
                                value={row.preparationOperationId}
                                onChange={e => updateRow(row._key, { preparationOperationId: e.target.value })}
                              >
                                <option value="">— жодна —</option>
                                {preparationOperations.map(op => (
                                  <option key={op.id} value={String(op.id)}>{op.name}</option>
                                ))}
                              </select>
                            </td>
                            <td data-label="Опис операції" style={{ ...td, padding: '8px 8px' }}>
                              <textarea
                                style={{ ...inp, padding: '5px', fontSize: '13px', minHeight: '34px', resize: 'vertical' }}
                                value={row.operationNotes}
                                onChange={e => updateRow(row._key, { operationNotes: e.target.value })}
                                placeholder="Опис операції..."
                              />
                            </td>
                            <td data-label="Видалити" style={{ ...td, padding: '8px 8px' }}>
                              <button type="button" onClick={() => removeRow(row._key)} style={{ ...buttonStyle('danger'), minWidth: '40px', minHeight: '40px', cursor: 'pointer', fontSize: '18px', lineHeight: 1 }}>×</button>
                            </td>
                          </tr>
                        );
                      })}
                      {rows.length === 0 && (
                        <tr><td colSpan={6 + Number(ingredientColumns.loss) + Number(ingredientColumns.price) + Number(ingredientColumns.cost) + Number(ingredientColumns.notes) + (ingredientColumns.processing ? 2 : 0)} style={{ ...td, padding: '20px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Натисніть "+ Рядок" щоб додати інгредієнт</td></tr>
                      )}
                    </tbody>
                    {rows.length > 0 && (
                      <tfoot>
                        <tr style={{ background: 'var(--crm-surface-alt)', fontWeight: 700 }}>
                          <td style={{ ...td, textAlign: 'right', padding: '10px 10px' }}>Σ Брутто: {totalBrutto.toFixed(3)}</td>
                          <td style={{ ...td, color: 'var(--crm-info)', padding: '10px 10px' }}>{totalNetto.toFixed(3)}</td>
                          <td style={{ ...td, color: 'var(--crm-info)', padding: '10px 10px' }}>{totalOutput.toFixed(3)}</td>
                          {ingredientColumns.loss && <td style={{ ...td, color: 'var(--crm-warning)', padding: '10px 10px' }}>{averageLossPercent.toFixed(2)}%</td>}
                          {ingredientColumns.price && <td style={{ padding: '10px 10px' }}></td>}
                          {ingredientColumns.cost && <td style={{ ...td, color: 'var(--crm-success)', fontSize: '14px', padding: '10px 10px' }}>{calcCost().toFixed(2)} ₴</td>}
                          {ingredientColumns.notes && <td style={{ padding: '10px 10px' }}></td>}
                          {ingredientColumns.processing && <td style={{ padding: '10px 10px' }}></td>}
                          {ingredientColumns.processing && <td style={{ padding: '10px 10px' }}></td>}
                          <td style={{ padding: '10px 10px' }}></td>
                          <td style={{ padding: '10px 10px' }}></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>

                <div className="recipe-scale-grid" style={{ padding: '10px 16px', background: 'var(--crm-info-bg)', borderTop: '1px solid var(--crm-border)', borderBottom: '1px solid var(--crm-border)', display: 'grid', gridTemplateColumns: '190px 180px auto', gap: '10px', alignItems: 'end' }}>
                  <label style={{ ...lbl, margin: 0 }}>
                    База перерахунку
                    <select style={inp} value={scaleBasis} onChange={(e) => setScaleBasis(e.target.value as 'brutto' | 'netto' | 'output')}>
                      <option value="brutto">Сума Брутто</option>
                      <option value="netto">Сума Нетто</option>
                      <option value="output">Сума Вихід</option>
                    </select>
                  </label>
                  <label style={{ ...lbl, margin: 0 }}>
                    Цільова вага
                    <input
                      type="number"
                      min="0"
                      step="0.001"
                      style={inp}
                      value={scaleTargetWeight}
                      onChange={(e) => setScaleTargetWeight(e.target.value)}
                      placeholder="Напр. 1.500"
                    />
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--crm-text-muted)' }}>
                      Поточна сума: <strong>{
                        scaleBasis === 'brutto'
                          ? totalBrutto.toFixed(3)
                          : (scaleBasis === 'netto' ? totalNetto.toFixed(3) : totalOutput.toFixed(3))
                      }</strong>
                    </span>
                    <button
                      type="button"
                      onClick={handleScaleIngredients}
                      style={{ ...buttonStyle('primary'), height: '38px', padding: '0 18px', cursor: 'pointer', fontWeight: 700, fontSize: '13px', justifySelf: 'start' }}
                    >
                      Перерахувати пропорційно
                    </button>
                  </div>
                </div>

                <div style={{ padding: '12px 16px', background: 'var(--crm-surface-alt)', borderTop: '1px solid var(--crm-border)', display: 'flex', gap: '24px', flexWrap: 'wrap', fontSize: '14px', color: 'var(--crm-text)' }}>
                  <span>Брутто: <strong>{totalBrutto.toFixed(3)}</strong></span>
                  <span>Нетто: <strong>{totalNetto.toFixed(3)}</strong></span>
                  <span>Вихід напівфабрикату (факт): <strong>{factualOutput.toFixed(3)}</strong></span>
                  <span>Прогнозований вихід (зі складу): <strong>{totalOutput.toFixed(3)}</strong></span>
                  <span>% втрат (заг.): <strong style={{ color: 'var(--crm-warning)' }}>{totalLossPercent.toFixed(2)}%</strong></span>
                  <span>% втрат (середній): <strong style={{ color: 'var(--crm-warning)' }}>{averageLossPercent.toFixed(2)}%</strong></span>
                  <span style={{ marginLeft: 'auto', fontSize: '15px', color: 'var(--crm-success)' }}>Собівартість: <strong>{calcCost().toFixed(2)} ₴</strong></span>
                </div>
              </div>

              <div style={{ background: 'var(--crm-warning-bg)', border: '1px solid var(--crm-warning)', borderRadius: 'var(--crm-radius-md)', padding: '12px 16px' }}>
                <label style={lbl}>
                  {form.isAuxiliaryRecipe ? 'Фізична вага набору (довідково; кількість наборів не визначає)' : 'Вихід напівфабрикату (факт)'}
                  <input
                    type="number"
                    min="0"
                    step="0.001"
                    style={{ ...inp, maxWidth: '320px' }}
                    value={form.finalOutput}
                    onChange={e => setForm({ ...form, finalOutput: e.target.value })}
                    placeholder={`Прогноз: ${totalOutput.toFixed(3)}`}
                  />
                </label>
              </div>

              {error && <div style={{ background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', padding: '10px', borderRadius: 'var(--crm-radius-sm)' }}>{error}</div>}

              <div style={{ border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', background: 'var(--crm-surface-alt)', padding: '12px 14px', display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--crm-text)', fontWeight: 600 }}>
                  <input type="checkbox" checked={saveArchiveSnapshot} onChange={(e) => setSaveArchiveSnapshot(e.target.checked)} />
                  Створити архівну копію при збереженні
                </label>
                {saveArchiveSnapshot && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--crm-text-muted)' }}>
                    Діє з дати:
                    <input type="date" value={archiveEffectiveFromUtc} onChange={(e) => setArchiveEffectiveFromUtc(e.target.value)} style={{ ...inp, minHeight: '34px', width: '170px' }} />
                  </label>
                )}
              </div>

              {archiveVersions.length > 0 && (
                <div style={{ ...panelStyle, padding: '12px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--crm-text)', marginBottom: '10px' }}>Архівні калькуляції</div>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {archiveVersions.slice(0, 5).map((version) => {
                      const isSelected = selectedArchiveVersionId === version.id;
                      return (
                        <div key={version.id} style={{ background: isSelected ? 'var(--crm-info-bg)' : 'var(--crm-surface)', border: `1px solid ${isSelected ? 'var(--crm-info)' : 'var(--crm-border)'}`, borderRadius: 'var(--crm-radius-md)', padding: '10px 12px', display: 'grid', gap: '8px', color: 'var(--crm-text)', boxShadow: 'var(--crm-shadow-sm)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--crm-text)' }}>v{version.versionNumber}</div>
                              <div style={{ fontSize: '11px', color: 'var(--crm-text-muted)' }}>Дата: {formatArchiveVersionDate(version.effectiveFromUtc)}</div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <button type="button" onClick={() => setSelectedArchiveVersionId(version.id)} style={{ ...buttonStyle('muted'), padding: '6px 10px', cursor: 'pointer', fontSize: '11px' }}>Вибрати</button>
                              <button type="button" onClick={() => restoreArchiveVersion(version)} style={{ ...buttonStyle('primary'), padding: '6px 10px', cursor: 'pointer', fontSize: '11px' }}>Відкрити</button>
                              <button type="button" onClick={() => deleteArchiveVersion(version)} style={{ ...buttonStyle('danger'), padding: '6px 10px', cursor: 'pointer', fontSize: '11px' }}>Видалити</button>
                            </div>
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--crm-text-muted)' }}>{version.isManualArchive ? 'Ручний архів' : 'Автоархів'}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                <input type="checkbox" checked={form.isAuxiliaryRecipe} onChange={e => setForm({
                  ...form, isAuxiliaryRecipe: e.target.checked,
                  excludeFromDishWeightCalc: e.target.checked || form.excludeFromDishWeightCalc,
                })} />
                Допоміжна рецептура — набір на порцію / тарілку
              </label>
              {form.isAuxiliaryRecipe && <p style={{ color: 'var(--crm-info)', fontSize: 13 }}>
                «1» у страві означає один набір, не кілограм. Компоненти цієї калькуляції задані на один набір;
                закупівля та списання рахують їх за кількістю наборів. Вага страви не збільшується.
                Історичні документи не перераховуються автоматично.
              </p>}
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: 'var(--crm-text)', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={form.excludeFromDishWeightCalc}
                  disabled={form.isAuxiliaryRecipe}
                  onChange={e => setForm({ ...form, excludeFromDishWeightCalc: e.target.checked })}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <span>Не рахувати вагу (Брутто/Нетто) у стравах</span>
              </label>

              <div className="recipe-editor-footer" style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', alignItems: 'center', paddingTop: '4px' }}>
                <button type="button" onClick={handlePrintModal}
                  style={{ ...buttonStyle('accent'), padding: '10px 18px', cursor: 'pointer', fontWeight: 500 }}>
                  🖨 Друкувати калькуляцію
                </button>
                <button type="button" onClick={handlePrintAccountingModal}
                  style={{ ...buttonStyle('success'), padding: '10px 18px', cursor: 'pointer', fontWeight: 500 }}>
                  Бухгалтерський друк
                </button>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="button" onClick={closeModal} style={{ ...buttonStyle('muted'), padding: '10px 20px', cursor: 'pointer' }}>Скасувати</button>
                  <button type="button" onClick={handleSave} disabled={saving} style={{ ...buttonStyle(saving ? 'muted' : 'success'), padding: '10px 24px', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                    {saving ? 'Збереження...' : (editingId ? 'Зберегти' : 'Створити')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {printManageModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 2050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ ...panelStyle, width: '100%', maxWidth: '900px', maxHeight: '85vh', overflow: 'auto', borderRadius: 'var(--crm-radius-lg)', padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: 'var(--crm-text)' }}>Керування виборами для друку Н/Ф</h3>
              <button type="button" onClick={() => setPrintManageModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
              <div style={{ border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: '12px', background: 'var(--crm-surface)' }}>
                <div style={{ fontWeight: 600, marginBottom: '8px', color: 'var(--crm-text)' }}>Поточний вибір</div>
                <div style={{ fontSize: '13px', color: 'var(--crm-text-muted)', marginBottom: '10px' }}>
                  Вибрано вручну: <strong>{selectedIds.length}</strong>, категорій: <strong>{selectedCategoryCount}</strong>, до друку: <strong>{resolvedPrintPreviewIds.length}</strong>
                </div>
                <label style={{ ...lbl, marginBottom: '8px' }}>
                  Назва набору
                  <input style={inp} value={printSelectionName} onChange={(e) => setPrintSelectionName(e.target.value)} placeholder="Наприклад: Бенкетні Н/Ф" />
                </label>
                <button type="button" onClick={saveCurrentPrintSelection} style={{ ...buttonStyle('primary'), padding: '8px 14px', cursor: 'pointer', fontWeight: 600 }}>
                  Зберегти поточний набір
                </button>
              </div>

              <div style={{ border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: '12px', background: 'var(--crm-surface)' }}>
                <div style={{ fontWeight: 600, marginBottom: '8px', color: 'var(--crm-text)' }}>Категорії для друку</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '220px', overflow: 'auto' }}>
                  {categories.map((category) => (
                    <label key={category.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--crm-text)' }}>
                      <input type="checkbox" checked={printCategoryIds.includes(category.id)} onChange={() => togglePrintCategory(category.id)} />
                      {category.name}
                    </label>
                  ))}
                  {categories.length === 0 && <span style={{ fontSize: '13px', color: 'var(--crm-text-muted)' }}>Категорії відсутні</span>}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button type="button" onClick={() => handlePrintSelectedBatch(selectedIds, printCategoryIds, false)} style={{ ...buttonStyle('accent'), padding: '9px 16px', cursor: 'pointer', fontWeight: 600 }}>
                🖨 Друк поточного вибору
              </button>
              <button type="button" onClick={() => handlePrintSelectedBatch(selectedIds, printCategoryIds, true)} style={{ ...buttonStyle('success'), padding: '9px 16px', cursor: 'pointer', fontWeight: 600 }}>
                Бухгалтерський друк
              </button>
            </div>

            <div style={{ borderTop: '1px solid var(--crm-border)', paddingTop: '12px' }}>
              <div style={{ fontWeight: 600, marginBottom: '8px', color: 'var(--crm-text)' }}>Збережені набори</div>
              {savedPrintSelections.length === 0 ? (
                <div style={{ fontSize: '13px', color: 'var(--crm-text-muted)' }}>Ще немає збережених наборів.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--crm-surface-alt)' }}>
                      <th style={{ ...td, fontWeight: 700 }}>Назва</th>
                      <th style={{ ...td, fontWeight: 700 }}>Вручну</th>
                      <th style={{ ...td, fontWeight: 700 }}>Категорії</th>
                      <th style={{ ...td, fontWeight: 700 }}>Дії</th>
                    </tr>
                  </thead>
                  <tbody>
                    {savedPrintSelections.map((selection) => (
                      <tr key={selection.id} style={{ borderTop: '1px solid var(--crm-table-row-border)' }}>
                        <td style={{ ...td, fontWeight: 500 }}>{selection.name}</td>
                        <td style={td}>{selection.selectedIds.length}</td>
                        <td style={td}>{selection.categoryIds.length}</td>
                        <td style={td}>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            <button type="button" onClick={() => applySavedSelection(selection)} style={{ ...buttonStyle('primary'), padding: '5px 9px', cursor: 'pointer' }}>Застосувати</button>
                            <button type="button" onClick={() => handlePrintSelectedBatch(selection.selectedIds, selection.categoryIds, false)} style={{ ...buttonStyle('accent'), padding: '5px 9px', cursor: 'pointer' }}>Друк</button>
                            <button type="button" onClick={() => handlePrintSelectedBatch(selection.selectedIds, selection.categoryIds, true)} style={{ ...buttonStyle('success'), padding: '5px 9px', cursor: 'pointer' }}>Бух.</button>
                            <button type="button" onClick={() => removeSavedSelection(selection.id)} style={{ ...buttonStyle('danger'), padding: '5px 9px', cursor: 'pointer' }}>Видалити</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {replaceSource && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ ...panelStyle, width: '100%', maxWidth: '560px', borderRadius: 'var(--crm-radius-lg)', padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: 'var(--crm-text)' }}>Масова заміна Н/Ф</h3>
              <button type="button" onClick={closeReplaceModal} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ color: 'var(--crm-text)', fontSize: '14px' }}>
              Замінити <strong>{replaceSource.name}</strong> у всіх стравах та напівфабрикатах на:
            </div>
            <SearchSelect
              items={replaceSemiSearchItems}
              value={replaceTargetId}
              onChange={setReplaceTargetId}
              placeholder="Оберіть напівфабрикат для заміни..."
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button type="button" onClick={closeReplaceModal} style={{ ...buttonStyle('muted'), padding: '10px 16px', cursor: 'pointer' }}>
                Скасувати
              </button>
              <button type="button" disabled={replacing} onClick={handleReplaceSemiFinished} style={{ ...buttonStyle(replacing ? 'muted' : 'warning'), padding: '10px 16px', cursor: replacing ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                {replacing ? 'Заміна...' : 'Замінити'}
              </button>
            </div>
          </div>
        </div>
      )}

      {usageModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ ...panelStyle, width: '100%', maxWidth: '760px', maxHeight: '80vh', overflow: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: 'var(--crm-text)' }}>{usageModal.title}</h3>
              <button type="button" onClick={() => setUsageModal(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            {usageModal.entries.length === 0 ? (
              <div style={{ color: 'var(--crm-text-muted)' }}>Немає входжень у стравах або напівфабрикатах.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--crm-surface-alt)' }}>
                    <th style={{ ...td, fontWeight: 700 }}>Тип</th>
                    <th style={{ ...td, fontWeight: 700 }}>ID</th>
                    <th style={{ ...td, fontWeight: 700 }}>Назва</th>
                    <th style={{ ...td, fontWeight: 700 }}>Кількість продукту/н/ф</th>
                  </tr>
                </thead>
                <tbody>
                  {usageModal.entries.map((entry) => (
                    <tr key={`${entry.kind}-${entry.id}`} style={{ borderTop: '1px solid var(--crm-table-row-border)' }}>
                      <td style={td}>{entry.kind === 'dish' ? 'Страва' : 'Н/Ф'}</td>
                      <td style={td}>{entry.id}</td>
                      <td style={{ ...td, fontWeight: 500 }}>{entry.name}</td>
                      <td style={td}>{entry.quantity.toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
      {deleteBlockModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ ...panelStyle, width: '100%', maxWidth: '760px', maxHeight: '80vh', overflow: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: 'var(--crm-danger)' }}>{deleteBlockModal.title}</h3>
              <button type="button" onClick={() => { setDeleteBlockModal(null); setDeleteReplaceTargetId(''); }} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', borderRadius: 'var(--crm-radius-md)', padding: '10px 12px', fontSize: '14px' }}>
              {deleteBlockModal.message}
            </div>
            {deleteBlockModal.entries.length === 0 ? (
              <div style={{ color: 'var(--crm-text-muted)' }}>Входження у стравах/Н-Ф не знайдено у поточному списку, але сервер заборонив видалення.</div>
            ) : (
              <>
                <div style={{ color: 'var(--crm-text)', fontWeight: 600 }}>Куди входить:</div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--crm-surface-alt)' }}>
                      <th style={{ ...td, fontWeight: 700 }}>Тип</th>
                      <th style={{ ...td, fontWeight: 700 }}>ID</th>
                      <th style={{ ...td, fontWeight: 700 }}>Назва</th>
                      <th style={{ ...td, fontWeight: 700 }}>Кількість продукту/н/ф</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deleteBlockModal.entries.map((entry) => (
                      <tr key={`delete-block-${entry.kind}-${entry.id}`} style={{ borderTop: '1px solid var(--crm-table-row-border)' }}>
                        <td style={td}>{entry.kind === 'dish' ? 'Страва' : 'Н/Ф'}</td>
                        <td style={td}>{entry.id}</td>
                        <td style={{ ...td, fontWeight: 500 }}>{entry.name}</td>
                        <td style={td}>{entry.quantity.toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            {deleteReplaceSourceSemi && (
              <div style={{ borderTop: '1px solid var(--crm-border)', paddingTop: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ color: 'var(--crm-text)', fontWeight: 600 }}>
                  Швидка заміна перед видаленням
                </div>
                <div style={{ fontSize: '13px', color: 'var(--crm-text-muted)' }}>
                  Оберіть Н/Ф-заміну для <strong>{deleteReplaceSourceSemi.name}</strong>, після чого система одразу спробує видалити поточний Н/Ф.
                </div>
                <SearchSelect
                  items={deleteReplaceSemiSearchItems}
                  value={deleteReplaceTargetId}
                  onChange={setDeleteReplaceTargetId}
                  placeholder="Оберіть Н/Ф для швидкої заміни..."
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    disabled={replacing}
                    onClick={handleReplaceFromDeleteModal}
                    style={{ ...buttonStyle(replacing ? 'muted' : 'warning'), padding: '10px 16px', cursor: replacing ? 'not-allowed' : 'pointer', fontWeight: 600 }}
                  >
                    {replacing ? 'Заміна...' : 'Замінити і видалити'}
                  </button>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => { setDeleteBlockModal(null); setDeleteReplaceTargetId(''); }} style={{ ...buttonStyle('muted'), padding: '10px 18px', cursor: 'pointer', fontWeight: 600 }}>
                Зрозуміло
              </button>
            </div>
          </div>
        </div>
      )}
      {b52ModalOpen && (
        <B52Modal
          title="Напівфабрикати"
          entityType="semifinished"
          onClose={() => setB52ModalOpen(false)}
          onSaved={() => dispatch(fetchSemiFinished())}
        />
      )}
    </div>
  );
};

export default SemiFinishedPage;
